import React, { useState, useEffect, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { availableIcons, MicIcon, HeadsetIcon, UserIcon, ClassicMicIcon } from "./SvgIcons";
import { backendUrl } from "./config.ts";

// Error Boundary component to catch ResizeObserver errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // If it's a ResizeObserver error, don't set hasError to true
    if (error && error.message && error.message.includes('ResizeObserver')) {
      return { hasError: false };
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Only log errors that aren't ResizeObserver related
    if (!error || !error.message || !error.message.includes('ResizeObserver')) {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return <div>Something went wrong.</div>;
    }
    return this.props.children;
  }
}

// Suppress ResizeObserver loop error (harmless development warning)
const originalError = console.error;
console.error = (...args) => {
  if (args[0] && typeof args[0] === 'string' && 
      (args[0].includes('ResizeObserver loop completed with undelivered notifications') ||
       args[0].includes('ResizeObserver loop limit exceeded'))) {
    return;
  }
  originalError.apply(console, args);
};

const API_BASE = `${backendUrl}/api`;

// Additional ResizeObserver suppression at window level
if (typeof window !== 'undefined') {
  const originalAddEventListener = window.addEventListener;
  window.addEventListener = function(type, listener, options) {
    if (type === 'error') {
      const wrappedListener = function(event) {
        if (event.message && event.message.includes('ResizeObserver')) {
          return;
        }
        return listener.call(this, event);
      };
      return originalAddEventListener.call(this, type, wrappedListener, options);
    }
    return originalAddEventListener.call(this, type, listener, options);
  };
  
  // Suppress ResizeObserver errors at the global error handler level
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (message && typeof message === 'string' && message.includes('ResizeObserver')) {
      return true; // Prevent the error from being logged
    }
    if (originalOnError) {
      return originalOnError.call(this, message, source, lineno, colno, error);
    }
    return false;
  };
  
  // Also suppress unhandled promise rejections that might contain ResizeObserver errors
  window.addEventListener('unhandledrejection', function(event) {
    if (event.reason && typeof event.reason === 'string' && event.reason.includes('ResizeObserver')) {
      event.preventDefault();
    }
  });
}

// Utility to expand 3-digit hex to 6-digit (use everywhere)
function expandHex(hex) {
  if (!hex) return '#000000'; // Return default black instead of empty string
  if (/^#[0-9A-Fa-f]{3}$/.test(hex)) {
    return '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  }
  if (/^#[0-9A-Fa-f]{6}$/.test(hex)) return hex;
  if (/^[0-9A-Fa-f]{3}$/.test(hex)) return '#' + hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  if (/^[0-9A-Fa-f]{6}$/.test(hex)) return '#' + hex;
  return hex;
}

// Utility to lighten a hex color
function lighten(hex, percent = 0.2) {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent * 100);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 + (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 + (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
}

// Smart color function to ensure good contrast
function getSmartButtonTextColor(bgColor, currentTextColor) {
  if (!bgColor || !currentTextColor) return currentTextColor || "#ffffff";
  
  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };
  
  // Calculate luminance
  const getLuminance = (r, g, b) => {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };
  
  // Calculate contrast ratio
  const getContrastRatio = (l1, l2) => {
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
  };
  
  const bgRgb = hexToRgb(bgColor);
  const textRgb = hexToRgb(currentTextColor);
  
  if (!bgRgb || !textRgb) return currentTextColor;
  
  const bgLuminance = getLuminance(bgRgb.r, bgRgb.g, bgRgb.b);
  const textLuminance = getLuminance(textRgb.r, textRgb.g, textRgb.b);
  const contrastRatio = getContrastRatio(bgLuminance, textLuminance);
  
  // If contrast is too low (< 3.0), suggest a better color
  if (contrastRatio < 3.0) {
    // If background is dark, use white text
    if (bgLuminance < 0.5) {
      return "#ffffff";
    } else {
      // If background is light, use dark text
      return "#000000";
    }
  }
  
  return currentTextColor;
}

// Smart color function to ensure good contrast between button and page background
function getSmartButtonBgColor(buttonBgColor, pageBgColor, primaryColor, secondaryColor) {
  if (!buttonBgColor || !pageBgColor) return buttonBgColor;
  
  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };
  
  // Calculate luminance
  const getLuminance = (r, g, b) => {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };
  
  // Calculate contrast ratio
  const getContrastRatio = (l1, l2) => {
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
  };
  
  const buttonRgb = hexToRgb(buttonBgColor);
  const pageRgb = hexToRgb(pageBgColor);
  
  if (!buttonRgb || !pageRgb) return buttonBgColor;
  
  const buttonLuminance = getLuminance(buttonRgb.r, buttonRgb.g, buttonRgb.b);
  const pageLuminance = getLuminance(pageRgb.r, pageRgb.g, pageRgb.b);
  const contrastRatio = getContrastRatio(buttonLuminance, pageLuminance);
  
  // If contrast is too low (< 2.0), suggest the opposite color (primary vs secondary)
  if (contrastRatio < 2.0) {
    // If page background is using primary color, use secondary for button
    if (pageBgColor === primaryColor) {
      return secondaryColor;
    } else if (pageBgColor === secondaryColor) {
      return primaryColor;
    } else {
      // If page background is custom, use the opposite of what button was trying to use
      if (buttonBgColor === primaryColor) {
        return secondaryColor;
      } else {
        return primaryColor;
      }
    }
  }
  
  return buttonBgColor;
}

function AdminPanel() {
  const { clientId: routeClientId } = useParams(); // Get clientId from route params
  const isClientMode = !!routeClientId; // If we have a clientId in the route, we're in client mode
  const navigate = useNavigate();
  
  const [form, setForm] = useState({
    clientId: "",
    companyName: "",
    email: "",
    agentId: "",
    hasRetellApiKey: false,
    hasGhlApiKey: false,
    primary: "#bbbbbb",
    secondary: "#000000",
    background: "#ffffff",
    transparentWidgetBg: true,
    bgGradient: false,
    buttonGradient: false,
    cardBg: "#e0e0e0",
    cardText: "#454545",
    buttonBg: "",
    buttonText: "#ffffff",
    buttonHover: "",
    transcriptDefaultOpenFull: true,
    transcriptDefaultOpenWidget: false,
    transparentBackgroundFull: false,
    transparentBackgroundWidget: true,
    fontFamily: "Montserrat, Arial, sans-serif",
    fontSize: "14",
    iconType: "classic-mic",
    iconColor: "#ffffff",
    avatarType: "avatar.png",
    showRoleIcons: false,
    minSize: 120,
    defaultSize: 160,
    maxSize: 190,
    layout: {
      widget: { mode: 'fixed', size: 160, min: 120, max: 190 },
      full: { mode: 'fixed', size: 160, min: 120, max: 190 },
    },
    showGlow: true,
    showWaves: true,
    glowSize: 1.2, // Multiplier of mic size
    glowBlur: 22, // Blur in pixels
    glowBrightness: 1.2, // Brightness multiplier
    glowOpacity: 0.8, // Opacity 0-1
    glowSpeed: 7.0, // Animation speed in seconds
    fullPageWidth: 800,
    fullPageHeight: 'auto',
    fullPageHeadline: "Get your (Offer Here)!",
    fullPageSubheadline: "Click to talk to our AI Assistant",
    fullPageFooter: "Â© 2025 Powered by Sellflows.com -- All rights reserved.",
    headlineFontSize: 28,
    subheadlineFontSize: 18,
    headlineColor: "primary",
    headlineCustomColor: "#6ec6ff",
    subheadlineColor: "secondary",
    subheadlineCustomColor: "#ff6ec4",
    footerColor: "secondary",
    footerCustomColor: "#ffffff",
    backgroundFull: "",
    buttonBgFull: "",
    buttonTextFull: "#ffffff",
    cardBgFull: "",
    cardTextFull: "",
    headlineBgType: "primary",
    headlineBgCustom: "",
    password: "",
    hideSensitiveFields: true,
    autoSendPasswordLink: false,
    backgroundFullType: "primary",
    buttonBgFullType: "primary",
    buttonTextFullType: "primary",
    cardBgFullType: "primary",
    cardTextFullType: "primary",
    backgroundFullCustom: "",
    buttonBgFullCustom: "",
    buttonTextFullCustom: "",
    cardBgFullCustom: "",
    cardTextFullCustom: "",
    transcriptFontSize: 14,
                  keyType: "client", // "shared" or "client" - default to client-specific for new clients
                  calendarId: "",
                  crmType: "none", // "none", "ghl", "hubspot", "salesforce", etc.
                  accountType: "client_specific", // "client_specific", "universal_agent", or "agency_api_specific_agent"
                  billingEnabled: false,
                  billingTier: "500min",
  });
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [adminKey, setAdminKey] = useState(localStorage.getItem('adminKey') || '');
  const [isAuthed, setIsAuthed] = useState(!!localStorage.getItem('adminKey'));
  const [clients, setClients] = useState({});
  const [editingClient, setEditingClient] = useState(null);
  const [activeTab, setActiveTab] = useState(isClientMode ? 'edit' : 'clients');
  const [clientSubTab, setClientSubTab] = useState('general');
  const [clientIdentifier, setClientIdentifier] = useState(routeClientId || '');
  const [clientPassword, setClientPassword] = useState('');
  const [clientAuthenticated, setClientAuthenticated] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Reset mode state
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetClientId, setResetClientId] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('recent'); // 'recent' or 'company'
  // Avatar/SVG upload state
  const [avatarFiles, setAvatarFiles] = useState(["avatar.png", "user.jpeg", "avatar-lowres.png"]);
  const [svgFiles, setSvgFiles] = useState([]);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingSVG, setUploadingSVG] = useState(false);
  const [showAvatarPreview, setShowAvatarPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState('widget');
  const [stickyPreview, setStickyPreview] = useState(true);
  const [stickyFullPagePreview, setStickyFullPagePreview] = useState(true);
  // Password setup state
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [setupPassword, setSetupPassword] = useState('');
  const [confirmSetupPassword, setConfirmSetupPassword] = useState('');
  const [setupError, setSetupError] = useState('');
  const [agencyName, setAgencyName] = useState('Sellflows');
  const [settings, setSettings] = useState({
    agencyName: 'Sellflows',
    favicon: null,
    subdomain: '',
    customDomain: '',
    logoLight: '/sellflows-logo-white.png',
    logoDark: '/sellflows-logo-dark.png'
  });
  const [deletingFile, setDeletingFile] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const isClientView = isClientMode || clientAuthenticated;

  // State for environment variable status
  const [envStatus, setEnvStatus] = useState({
    retellApiKey: false,
    agentId: false
  });
  
  // Track if client has been saved after switching to universal agent
  const [universalAgentSaved, setUniversalAgentSaved] = useState(false);

  // Add animated rotating glow CSS to the preview
  const glowAnimation = `@keyframes adminRotateGlow {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}`;

  // Sync preview mode with current sub-tab
  useEffect(() => {
    setPreviewMode(clientSubTab === 'general' ? 'widget' : 'full');
  }, [clientSubTab]);

  // Check environment variable status
  const checkEnvStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/env-status`, {
        headers: {
          'x-admin-key': adminKey
        }
      });
      if (response.ok) {
        const data = await response.json();
        setEnvStatus(data);
      }
    } catch (error) {
      console.error('Error checking env status:', error);
    }
  };

  // Load files when Settings tab is accessed
  useEffect(() => {
    if (!isClientView && activeTab === 'settings') {
      fetchAvatars();
      fetchSVGs();
    }
  }, [activeTab, isClientView]);

  // Lock client mode to edit tab only
  useEffect(() => {
    if (isClientView && activeTab !== 'edit') {
      setActiveTab('edit');
    }
  }, [isClientView, activeTab]);

  // Check environment status when admin panel loads
  useEffect(() => {
    if (isAuthed) {
      checkEnvStatus();
    }
  }, [isAuthed]);

  // Check if client needs password setup when in client mode
  useEffect(() => {
    if (isClientMode && routeClientId && !clientAuthenticated && !showPasswordSetup) {
      fetch(`${API_BASE}/client-config?client=${routeClientId}`)
        .then(res => res.json())
        .then(data => {
          // Show setup only when backend explicitly reports no password.
          // Do not infer from missing password fields (those are intentionally hidden).
          if (data && data.hasPassword === false) {
            setShowPasswordSetup(true);
            // Load client configuration for the setup form
            setForm({
              clientId: data.clientId || routeClientId,
              companyName: data.companyName || '',
              agentId: data.agentId || '',
              primary: data.ui?.colors?.primary || "#bbbbbb",
              secondary: data.ui?.colors?.secondary || "#000000",
              background: data.ui?.background || "#ffffff",
              transparentWidgetBg: data.ui?.transparentWidgetBg ?? true,
              bgGradient: data.ui?.bgGradient ?? false,
              buttonGradient: data.ui?.buttonGradient ?? false,
              cardBg: data.ui?.cardBg || "#e0e0e0",
              cardText: data.ui?.cardText || "#454545",
              buttonBg: data.ui?.buttonBg || "",
              buttonText: data.ui?.buttonText || "#ffffff",
              buttonHover: data.ui?.buttonHover || "",
              transcriptDefaultOpenFull: data.ui?.transcriptDefaultOpenFull ?? true,
              transcriptDefaultOpenWidget: data.ui?.transcriptDefaultOpenWidget ?? false,
              transparentBackgroundFull: data.ui?.transparentBackgroundFull ?? false,
              transparentBackgroundWidget: data.ui?.transparentBackgroundWidget ?? true,
              fontFamily: data.ui?.fontFamily || "Montserrat, Arial, sans-serif",
              fontSize: data.ui?.fontSize || "14",
              iconType: data.ui?.icon?.type || "classic-mic",
              iconColor: data.ui?.icon?.color || "#ffffff",
              avatarType: data.ui?.avatar || "avatar.png",
              showRoleIcons: data.ui?.showRoleIcons ?? false,
              minSize: data.ui?.layout?.minSize || 120,
              defaultSize: data.ui?.layout?.defaultSize || 160,
              maxSize: data.ui?.layout?.maxSize || 190,
              layout: {
                widget: data.ui?.layout?.widget || { mode: 'fixed', size: 160, min: 120, max: 190 },
                full: data.ui?.layout?.full || { mode: 'fixed', size: 160, min: 120, max: 190 },
              },
              showGlow: data.ui?.showGlow ?? true,
              showWaves: data.ui?.showWaves ?? true,
              fullPageWidth: data.ui?.fullPageWidth || 800,
              fullPageHeight: data.ui?.fullPageHeight || 'auto',
              fullPageHeadline: data.ui?.fullPageHeadline || "Get your (Offer Here)!",
              fullPageSubheadline: data.ui?.fullPageSubheadline || "Click to talk to our AI Assistant",
              fullPageFooter: data.ui?.fullPageFooter || "Â© 2025 Powered by Sellflows.com -- All rights reserved.",
              headlineFontSize: data.ui?.headlineFontSize || 28,
              subheadlineFontSize: data.ui?.subheadlineFontSize || 18,
              headlineColor: data.ui?.headlineColor || "primary",
              headlineCustomColor: data.ui?.headlineCustomColor || "#6ec6ff",
              subheadlineColor: data.ui?.subheadlineColor || "secondary",
              subheadlineCustomColor: data.ui?.subheadlineCustomColor || "#ff6ec4",
              footerColor: data.ui?.footerColor || "secondary",
              footerCustomColor: data.ui?.footerCustomColor || "#ffffff",
              backgroundFull: data.ui?.backgroundFull || "",
              primaryFull: data.ui?.colors?.primary || "",
              secondaryFull: data.ui?.colors?.secondary || "",
              buttonBgFull: data.ui?.buttonBg || "",
              buttonTextFull: data.ui?.buttonText || "#ffffff",
              cardBgFull: data.ui?.cardBg || "#e0e0e0",
              cardTextFull: data.ui?.cardText || "#454545",
              email: data.email || "",
              password: "",
              hideSensitiveFields: true,
              autoSendPasswordLink: false,
              backgroundFullType: data.ui?.backgroundFullType || "primary",
              buttonBgFullType: data.ui?.buttonBgFullType || "primary",
              buttonTextFullType: data.ui?.buttonTextFullType || "primary",
              cardBgFullType: data.ui?.cardBgFullType || "primary",
              cardTextFullType: data.ui?.cardTextFullType || "primary",
              backgroundFullCustom: data.ui?.backgroundFullCustom || "",
              buttonBgFullCustom: data.ui?.buttonBgFullCustom || "",
              buttonTextFullCustom: data.ui?.buttonTextFullCustom || "",
              cardBgFullCustom: data.ui?.cardBgFullCustom || "",
              cardTextFullCustom: data.ui?.cardTextFullCustom || "",
              transcriptFontSize: 14,
            });
          }
        })
        .catch(err => {
          console.error('Error checking client password setup:', err);
        });
    }
  }, [isClientMode, routeClientId, clientAuthenticated, showPasswordSetup]);

  useEffect(() => {
    if (routeClientId) {
      setClientIdentifier(routeClientId);
    }
  }, [routeClientId]);
  
  // Check for reset mode parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetParam = urlParams.get('reset');
    const clientIdParam = urlParams.get('clientId');
    
    if (resetParam === 'true' && (clientIdParam || routeClientId)) {
      setIsResetMode(true);
      setResetClientId(clientIdParam || routeClientId || '');
    }
  }, [routeClientId]);

  // Fetch all clients
  const fetchClients = () => {
    if (!adminKey) {
      setError('Admin authentication required');
      return;
    }
    
    fetch(`${API_BASE}/clients`, {
      headers: {
        'x-admin-key': adminKey
      }
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        setClients(data || {});
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching clients:', err);
        setError('Failed to fetch clients');
        setLoading(false);
      });
  };

  // Fetch avatars and SVGs
  const fetchAvatars = () => {
    fetch(`${API_BASE}/avatars`)
      .then(res => res.json())
      .then(files => {
        if (Array.isArray(files)) {
          setAvatarFiles(files);
        } else {
          setAvatarFiles([]);
        }
      })
      .catch(err => {
        console.error('Error fetching avatars:', err);
        setAvatarFiles([]);
      });
  };
  const fetchSVGs = () => {
    fetch(`${API_BASE}/svgs`)
      .then(res => res.json())
      .then(data => setSvgFiles(data || []))
      .catch(err => console.error('Error fetching SVGs:', err));
  };

  useEffect(() => {

    // Test backend connection first
    fetch(`${API_BASE}/clients`, {
      headers: {
        'x-admin-key': adminKey || 'V!BNCjzjjuOUHCb5'
      }
    })
    .then(res => {
      console.log('Backend connection test - Status:', res.status);
      if (res.ok) {
        console.log('Backend connection successful, fetching data...');
    fetchClients();
    fetchAvatars();
    fetchSVGs();
      } else {
        console.error('Backend not accessible or unauthorized');
        setLoading(false);
      }
    })
    .catch(err => {
      console.error('Backend connection failed:', err);
      setLoading(false);
    });
  }, []);

  // Preview background and mic circle background calculations
  const previewBg = previewMode === 'widget'
    ? (form.transparentWidgetBg ? 'transparent' : (form.bgGradient ? `linear-gradient(135deg, ${expandHex(form.primary)} 0%, ${expandHex(form.secondary)} 100%)` : expandHex(form.background)))
    : (form.bgGradient ? `linear-gradient(135deg, ${expandHex(form.primary)} 0%, ${expandHex(form.secondary)} 100%)` : (form.backgroundFullType === 'primary' ? expandHex(form.primary) :
       form.backgroundFullType === 'secondary' ? expandHex(form.secondary) :
       expandHex(form.backgroundFullCustom || form.backgroundFull || form.background)));

  // In the preview, compute the mic button background: gradient if on, else primary color
  const micCircleBg = previewMode === 'widget' 
    ? (form.buttonGradient ? `linear-gradient(315deg, ${expandHex(form.primary)} 0%, ${expandHex(form.secondary)} 100%)` : expandHex(form.primary))
    : (form.buttonGradient ? `linear-gradient(315deg, ${expandHex(form.primary)} 0%, ${expandHex(form.secondary)} 100%)` : expandHex(form.primary));

  // At the top of the AdminPanel component, inject the keyframes globally (only once)
  useEffect(() => {
    if (!document.getElementById('admin-preview-glow-anim')) {
      const style = document.createElement('style');
      style.id = 'admin-preview-glow-anim';
      style.innerHTML = `@keyframes adminRotateGlow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
      document.head.appendChild(style);
    }
    
    // Handle ResizeObserver errors
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return; // Suppress ResizeObserver errors
      }
      originalError.apply(console, args);
    };
    
    return () => {
      console.error = originalError;
    };
  }, []);

  // Handle form input
  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    
    console.log('handleChange called:', { name, value, type, checked });
    
    setForm(f => {
      let newValue = type === "checkbox" ? checked : value;
      
      // Handle nested properties like "layout.full.mode"
      if (name.includes('.')) {
        const parts = name.split('.');
        const newForm = { ...f };
        let current = newForm;
        
        // Navigate to the nested object
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          }
          current = current[parts[i]];
        }
        
        // Set the final property
        current[parts[parts.length - 1]] = newValue;
        return newForm;
      }
      
      // If this is a color field, always expand to 6-digit hex
      if ([
        'primary', 'secondary', 'background', 'cardBg', 'cardText',
        'buttonBg', 'buttonText', 'buttonHover', 'iconColor',
        'headlineCustomColor', 'subheadlineCustomColor', 'footerCustomColor',
        'primaryFull', 'secondaryFull', 'backgroundFullCustom',
        'buttonBgFullCustom', 'buttonTextFullCustom', 'cardBgFullCustom', 'cardTextFullCustom'
      ].includes(name)) {
        newValue = expandHex(newValue);
      }
      // For fontSize, always store as string
      if (name === 'fontSize') newValue = String(newValue);
      // For fullPageWidth, always store as number
      if (name === 'fullPageWidth') newValue = Number(newValue);
      // For glow fields, always store as number
      if (['glowSize', 'glowBlur', 'glowBrightness', 'glowOpacity', 'glowSpeed'].includes(name)) {
        newValue = Number(newValue);
      }
      return { ...f, [name]: newValue };
    });
    
    // Mark as having changes
    setHasChanges(true);
  };

  // Handle add or update
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.clientId || !String(form.clientId).trim()) {
      setError("Client ID is required.");
      return;
    }

    // Guard rails for the new agency shared-key mode
    if (form.accountType === 'agency_api_specific_agent' && !(form.tempAgentId || form.agentId)) {
      setError("Agency API + Specific Agent mode requires a client Agent ID.");
      return;
    }

    try {
          const config = {
        clientId: form.clientId,
        companyName: form.companyName,
        email: form.email,
        agentId: form.agentId,
        keyType: form.keyType, // "shared" or "client"
        accountType: form.accountType, // "client_specific", "universal_agent", or "agency_api_specific_agent"
        // Add temporary API keys if client-specific keys are used
        ...(form.keyType === 'client' && form.tempRetellKey && { apiKey: form.tempRetellKey }),
        ...(form.tempAgentId && { agentId: form.tempAgentId }),
        ...(form.tempGhlKey && { ghlApiKey: form.tempGhlKey }),
        password: form.password,
        hideSensitiveFields: form.hideSensitiveFields,
      ui: {
        colors: {
            primary: form.primary,
            secondary: form.secondary,
            background: form.background,
            backgroundFull: form.backgroundFullCustom || form.background,
            backgroundWidget: form.background,
            cardBg: form.cardBg,
            cardText: form.cardText,
            buttonBg: form.buttonBg,
            buttonText: form.buttonText,
            buttonHover: form.buttonHover
          },
          transparentWidgetBg: form.transparentWidgetBg,
          bgGradient: form.bgGradient,
          buttonGradient: form.buttonGradient,
          transcriptDefaultOpenFull: form.transcriptDefaultOpenFull,
          transcriptDefaultOpenWidget: form.transcriptDefaultOpenWidget,
          transparentBackgroundFull: form.transparentBackgroundFull,
          transparentBackgroundWidget: form.transparentBackgroundWidget,
        fonts: {
            family: form.fontFamily,
            size: form.fontSize
        },
        icon: {
            type: form.iconType,
            color: form.iconColor
          },
          avatar: form.avatarType,
          showRoleIcons: form.showRoleIcons,
          layout: form.layout,
          showGlow: form.showGlow,
          showWaves: form.showWaves,
          glowSize: form.glowSize,
          glowBlur: form.glowBlur,
          glowBrightness: form.glowBrightness,
          glowOpacity: form.glowOpacity,
          glowSpeed: form.glowSpeed,
          fullPageWidth: form.fullPageWidth,
          fullPageHeight: form.fullPageHeight,
          fullPageHeadline: form.fullPageHeadline,
          fullPageSubheadline: form.fullPageSubheadline,
          fullPageFooter: form.fullPageFooter,
          headlineFontSize: form.headlineFontSize,
          headlineColor: form.headlineColor,
          headlineCustomColor: form.headlineCustomColor,
          headlineBgType: form.headlineBgType,
          headlineBgCustom: form.headlineBgCustom,
          subheadlineColor: form.subheadlineColor,
          subheadlineCustomColor: form.subheadlineCustomColor,
          footerColor: form.footerColor,
          footerCustomColor: form.footerCustomColor,
          backgroundFullType: form.backgroundFullType,
          buttonBgFullType: form.buttonBgFullType,
          buttonTextFullType: form.buttonTextFullType,
          cardBgFullType: form.cardBgFullType,
          cardTextFullType: form.cardTextFullType,
          backgroundFullCustom: form.backgroundFullCustom,
          buttonBgFullCustom: form.buttonBgFullCustom,
          buttonTextFullCustom: form.buttonTextFullCustom,
          cardBgFullCustom: form.cardBgFullCustom,
          cardTextFullCustom: form.cardTextFullCustom,
          transcriptFontSize: form.transcriptFontSize,
                        buttonTextContent: form.buttonTextContent,
              buttonTextSize: form.buttonTextSize || 14,
        }
      };

      if (clientAuthenticated) {
        // Client mode - use client update endpoint
        const response = await fetch(`${API_BASE}/client-update`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientId: routeClientId,
            config: config
          }),
        });

        const data = await response.json();

        if (response.ok) {
                      setSuccess("Configuration updated successfully!");
            // Auto-clear success message after 5 seconds
            setTimeout(() => setSuccess(""), 5000);
          setHasChanges(false); // Reset change tracking
          // Keep form populated in client mode
        } else {
          setError(data.message || "Failed to update configuration");
        }
      } else {
        // Admin mode - use existing admin endpoint
        const requestBody = editId ? 
          { config: config, clientId: form.clientId } : // For updates, send config and new clientId
          { clientId: form.clientId, config: config }; // For new clients, send both
        
        const response = await fetch(`${API_BASE}/clients${editId ? `/${editId}` : ''}`, {
          method: editId ? "PUT" : "POST",
          headers: { 
            "Content-Type": "application/json",
            "x-admin-key": adminKey
          },
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();

	        if (response.ok) {
	          if (editId) {
            setSuccess("Client updated successfully!");
            // Auto-clear success message after 5 seconds
            setTimeout(() => setSuccess(""), 5000);
            setHasChanges(false); // Reset change tracking
            // Set universal agent saved flag if switching to universal
            if (form.accountType === 'universal_agent' || form.accountType === 'agency_api_specific_agent') {
              setUniversalAgentSaved(true);
            }
            // Refresh clients list to ensure updated data is loaded
            fetchClients();
            // Keep form populated when editing - DON'T clear editId
	          } else {
              const createdClientId = form.clientId;
	            setSuccess("Client added successfully!");
	            // Auto-clear success message after 5 seconds
	            setTimeout(() => setSuccess(""), 5000);
            
            // Clear temporary API keys from memory for security
	            setForm(prev => {
	              const { tempRetellKey, tempAgentId, tempGhlKey, ...cleanForm } = prev;
	              return cleanForm;
	            });
            
            // Auto-send password link if enabled
            if (form.autoSendPasswordLink) {
              try {
                const linkResponse = await fetch(`${API_BASE}/generate-password-link`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'x-admin-key': adminKey
                  },
                  body: JSON.stringify({ clientId: form.clientId })
                });

                const linkData = await linkResponse.json();

                if (linkResponse.ok && linkData.success) {
                  await navigator.clipboard.writeText(linkData.link);
                  setSuccess(`Client added successfully! ${linkData.message} - Link copied to clipboard!`);
                  // Auto-clear success message after 5 seconds
                  setTimeout(() => setSuccess(""), 5000);
                } else {
                  setSuccess(`Client added successfully! (Password link: ${linkData.message})`);
                  // Auto-clear success message after 5 seconds
                  setTimeout(() => setSuccess(""), 5000);
                }
              } catch (error) {
                console.error('Error auto-sending password link:', error);
                setSuccess("Client added successfully! (Password link generation failed)");
                // Auto-clear success message after 5 seconds
                setTimeout(() => setSuccess(""), 5000);
              }
            }
            
	            setForm({
	              clientId: "",
	              companyName: "",
	              email: "",
	              agentId: "",
	              hasRetellApiKey: false,
	              hasGhlApiKey: false,
	              keyType: "client",
	              accountType: "client_specific",
	              calendarId: "",
	              crmType: "none",
	              billingEnabled: false,
	              billingTier: "500min",
	              primary: "#bbbbbb",
	              secondary: "#000000",
	              background: "#aaaaaa",
              transparentWidgetBg: true,
              bgGradient: false,
              buttonGradient: false,
              cardBg: "#e0e0e0",
              cardText: "#454545",
              buttonBg: "",
              buttonText: "#ffffff",
              buttonHover: "",
              transcriptDefaultOpenFull: true,
              transcriptDefaultOpenWidget: false,
              transparentBackgroundFull: false,
              transparentBackgroundWidget: true,
              fontFamily: "Montserrat, Arial, sans-serif",
              fontSize: "14",
              iconType: "classic-mic",
              iconColor: "#ffffff",
              avatarType: "avatar.png",
              showRoleIcons: false,
              minSize: 120,
              defaultSize: 160,
              maxSize: 190,
              layout: {
                widget: { mode: 'fixed', size: 160, min: 120, max: 190 },
                full: { mode: 'fixed', size: 160, min: 120, max: 190 },
              },
              showGlow: true,
              showWaves: true,
              glowSize: 1.2,
              glowBlur: 22,
              glowBrightness: 1.2,
              glowOpacity: 0.8,
              glowSpeed: 7.0,
              fullPageWidth: 800,
              fullPageHeight: 'auto',
              fullPageHeadline: "Get your (Offer Here)!",
              fullPageSubheadline: "Click to talk to our AI Assistant",
              fullPageFooter: "© 2025 Powered by Sellflows.com -- All rights reserved.",
              headlineFontSize: 28,
              subheadlineFontSize: 18,
              headlineColor: "primary",
              headlineCustomColor: "#6ec6ff",
              headlineBgType: "primary",
              headlineBgCustom: "#ffffff",
              subheadlineColor: "secondary",
              subheadlineCustomColor: "#ff6ec4",
              footerColor: "secondary",
              footerCustomColor: "#ffffff",
              backgroundFull: "",
              buttonBgFull: "",
              buttonTextFull: "#ffffff",
              cardBgFull: "",
              cardTextFull: "",
              password: "",
              hideSensitiveFields: true,
              autoSendPasswordLink: false,
              backgroundFullType: "primary",
              buttonBgFullType: "primary",
              buttonTextFullType: "primary",
              cardBgFullType: "primary",
              cardTextFullType: "primary",
              backgroundFullCustom: "",
              buttonBgFullCustom: "",
              buttonTextFullCustom: "",
              cardBgFullCustom: "",
              cardTextFullCustom: "",
              transcriptFontSize: 14,
              buttonTextContent: "Talk to AI Assistant",
	              buttonTextSize: 14,
	            });
              // After creating a client, return to list view to avoid blank edit state.
              setHasChanges(false);
              setEditId(null);
              setClientSubTab('general');
              setActiveTab('clients');
              // Keep search focused on the newly created client so it's easy to reopen.
              setSearch(createdClientId);
	          }
	            fetchClients();
        } else {
          console.error('Backend save error:', data);
          setError(data.message || data.error || "Failed to save client");
          }
      }
    } catch (error) {
      console.error('Save error details:', error);
      const message = error?.message || error?.toString() || "Unknown error";
      if (message.includes('Failed to fetch')) {
        setError(`Error saving client: Cannot reach backend at ${API_BASE}. Make sure the backend server is running on port 8080.`);
      } else {
        setError(`Error saving client: ${message}`);
      }
    }
  };

  // Handle edit
  const handleEdit = id => {
    console.log('handleEdit called with id:', id);
    const c = clients[id];
    console.log('client data:', c);
    setEditId(id);
    console.log('setEditId called with:', id);
    handleTabChange('edit'); // Switch to edit tab
    setForm({
      clientId: id,
      companyName: c.companyName || "",
      email: c.email || "",
      agentId: c.agentId,
      hasRetellApiKey: !!c.hasRetellApiKey,
      hasGhlApiKey: !!c.hasGhlApiKey,
      password: c.password || "",
      autoSendPasswordLink: c.autoSendPasswordLink || false,
                    primary: c.ui?.colors?.primary || "#3a4f90",
              secondary: c.ui?.colors?.secondary || "#19647e",
              background: c.ui?.colors?.background || "#ffffff",
      transparentWidgetBg: c.ui?.transparentWidgetBg ?? true,
      bgGradient: c.ui?.bgGradient ?? false,
      buttonGradient: c.ui?.buttonGradient ?? false,
      cardBg: c.ui?.colors?.cardBg || "#e0e0e0",
      cardText: c.ui?.colors?.cardText || "#454545",
      buttonBg: c.ui?.colors?.buttonBg || "",
      buttonText: c.ui?.colors?.buttonText || "#ffffff",
      buttonHover: c.ui?.colors?.buttonHover || "",
      transcriptDefaultOpenFull: c.ui?.transcriptDefaultOpenFull ?? true,
      transcriptDefaultOpenWidget: c.ui?.transcriptDefaultOpenWidget ?? false,
      transparentBackgroundFull: c.ui?.colors?.backgroundFull === "transparent",
      transparentBackgroundWidget: c.ui?.colors?.backgroundWidget === "transparent",
      fontFamily: c.ui?.fonts?.family || "Montserrat, Arial, sans-serif",
      fontSize: c.ui?.fonts?.size || "14",
      iconType: c.ui?.icon?.type || "classic-mic",
      iconColor: c.ui?.icon?.color || "#ffffff",
      avatarType: c.ui?.avatar || "avatar.png",
      showRoleIcons: c.ui?.showRoleIcons ?? false,
      minSize: c.ui?.layout?.minSize || 120,
      defaultSize: c.ui?.layout?.defaultSize || 160,
      maxSize: c.ui?.layout?.maxSize || 190,
      layout: {
        widget: c.ui?.layout?.widget || { mode: 'fixed', size: 160, min: 120, max: 190 },
        full: c.ui?.layout?.full || { mode: 'fixed', size: 160, min: 120, max: 190 },
      },
      showGlow: c.ui?.showGlow ?? true,
      showWaves: c.ui?.showWaves ?? true,
      glowSize: c.ui?.glowSize || 1.2,
      glowBlur: c.ui?.glowBlur || 22,
      glowBrightness: c.ui?.glowBrightness || 1.2,
      glowOpacity: c.ui?.glowOpacity || 0.8,
      glowSpeed: c.ui?.glowSpeed || 7.0,
      fullPageWidth: c.ui?.fullPageWidth || 800,
      fullPageHeight: c.ui?.fullPageHeight || 'auto',
      fullPageHeadline: c.ui?.fullPageHeadline || "Get your (Offer Here)!",
      fullPageSubheadline: c.ui?.fullPageSubheadline || "Click to talk to our AI Assistant",
      fullPageFooter: c.ui?.fullPageFooter || "Â© 2025 Powered by Sellflows.com -- All rights reserved.",
      headlineFontSize: c.ui?.headlineFontSize || 28,
      subheadlineFontSize: c.ui?.subheadlineFontSize || 18,
      headlineColor: c.ui?.headlineColor || "primary",
      headlineCustomColor: c.ui?.headlineCustomColor || "#6ec6ff",
      headlineBgType: c.ui?.headlineBgType || "primary",
      headlineBgCustom: c.ui?.headlineBgCustom || "#ffffff",
      subheadlineColor: c.ui?.subheadlineColor || "secondary",
      subheadlineCustomColor: c.ui?.subheadlineCustomColor || "#ff6ec4",
      footerColor: c.ui?.footerColor || "secondary",
      footerCustomColor: c.ui?.footerCustomColor || "#ffffff",
      backgroundFull: c.ui?.backgroundFull || "",
      buttonBgFull: c.ui?.buttonBgFull || "",
      buttonTextFull: c.ui?.buttonTextFull || "#ffffff",
      cardBgFull: c.ui?.cardBgFull || "",
      cardTextFull: c.ui?.cardTextFull || "",
      hideSensitiveFields: c.hideSensitiveFields ?? true,
      backgroundFullType: c.ui?.backgroundFullType || "primary",
      buttonBgFullType: c.ui?.buttonBgFullType || "primary",
      buttonTextFullType: c.ui?.buttonTextFullType || "primary",
      cardBgFullType: c.ui?.cardBgFullType || "primary",
      cardTextFullType: c.ui?.cardTextFullType || "primary",
      backgroundFullCustom: c.ui?.backgroundFullCustom || "",
      buttonBgFullCustom: c.ui?.buttonBgFullCustom || "",
      buttonTextFullCustom: c.ui?.buttonTextFullCustom || "",
      cardBgFullCustom: c.ui?.cardBgFullCustom || "",
      cardTextFullCustom: c.ui?.cardTextFullCustom || "",
      transcriptFontSize: c.ui?.transcriptFontSize || 14,
      accountType: c.accountType || "client_specific",
      keyType: c.keyType || "client",
      billingEnabled: c.billingEnabled ?? false,
      billingTier: c.billingTier || "500min",
                  buttonTextContent: c.ui?.buttonTextContent || "Talk to AI Assistant",
            buttonTextSize: c.ui?.buttonTextSize || 14,
    });
    
    // Set shared-credentials saved state for modes that use agency shared API key
    if (c.accountType === 'universal_agent' || c.accountType === 'agency_api_specific_agent') {
      setUniversalAgentSaved(true);
    } else {
      setUniversalAgentSaved(false);
    }
  };

  useEffect(() => {
    if (isClientMode) return;

    const editClientId = new URLSearchParams(window.location.search).get('editClient');
    if (!editClientId || editId === editClientId) return;
    if (!clients[editClientId]) return;

    handleEdit(editClientId);
  }, [clients, editId, isClientMode]);

  // Handle delete
  const handleDelete = id => {
    if (!window.confirm(`Delete client ${id}?`)) return;
    fetch(`${API_BASE}/clients/${id}`, { method: "DELETE", headers: { "x-admin-key": adminKey } })
      .then(res => res.json())
      .then(data => {
        if (data.error) setError(data.error);
        else {
          setSuccess("Client deleted!");
          // Auto-clear success message after 5 seconds
          setTimeout(() => setSuccess(""), 5000);
          fetchClients();
        }
      });
  };

  const handleLogin = async () => {
    if (!adminKey.trim()) {
      setError('Please enter admin password');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/clients`, {
        method: 'GET',
        headers: {
          'x-admin-key': adminKey
        }
      });
      
      if (response.ok) {
    localStorage.setItem('adminKey', adminKey);
    setIsAuthed(true);
        setError('');
        fetchClients();
      } else {
        setError('Invalid admin password');
        setIsAuthed(false);
      }
    } catch (error) {
      setError('Login failed. Please try again.');
      setIsAuthed(false);
    }
  };

  // Avatar upload handler
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append('avatar', file);
    const res = await fetch(`${API_BASE}/upload/avatar`, {
      method: 'POST',
      headers: { 'x-admin-key': adminKey },
      body: formData
    });
    setUploadingAvatar(false);
    if (res.ok) {
      fetchAvatars();
              setSuccess('Avatar uploaded!');
        // Auto-clear success message after 5 seconds
        setTimeout(() => setSuccess(""), 5000);
    } else {
      setError('Failed to upload avatar.');
    }
  };




  // Wrap the main return in an auth check
  if (!isAuthed && !isClientMode) {
    return (
      <div style={{ background: "#3a4f90", minHeight: "100vh", paddingTop: 40 }}>
        <div style={{ maxWidth: 400, margin: "0 auto", padding: 24, background: "#fff", borderRadius: 12 }}>
          <h2>Admin Login</h2>
          {error && (
            <div style={{ color: "red", marginBottom: 12, fontSize: 14 }}>
              {error}
            </div>
          )}
          <input
            type="password"
            placeholder="Admin Password"
            value={adminKey}
            onChange={e => setAdminKey(e.target.value)}
            style={{ width: "100%", padding: 8, marginBottom: 12 }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleLogin();
              }
            }}
          />
          <button onClick={handleLogin}>Login</button>
        </div>
      </div>
    );
  }

  const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
  
  // Use actual layout settings for preview
  const layout = form.layout?.[previewMode === 'full' ? 'full' : 'widget'] || { mode: 'fixed', size: 160, min: 120, max: 190 };
  const micSize = layout.mode === 'fixed' ? Number(layout.size) : clamp(160, Number(layout.min), Number(layout.max));
  
  const glowSize = micSize + 32;
  const waveBase = micSize;

  // In the preview, when in full page mode, set maxWidth/maxHeight for the preview panel
  const previewPanelStyle = {
    flex: '0 1 50%',
    minWidth: 260,
    maxWidth: previewMode === 'full' ? Math.min(Number(form.fullPageWidth || 800), 500) : undefined,
    maxHeight: previewMode === 'full' && form.fullPageHeight !== 'auto' ? Number(form.fullPageHeight) : undefined,
    background: previewBg,
    borderRadius: 10,
    boxShadow: '0 2px 8px #0001',
    padding: 18,
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  // Client authentication functions
  const handleClientLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    const identifier = (clientIdentifier || routeClientId || '').trim();
    if (!identifier || !clientPassword.trim()) {
      setLoginError('Please enter Email or Client ID and password');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/client-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier,
          clientId: routeClientId,
          password: clientPassword
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setClientAuthenticated(true);
        handleTabChange('edit');
        // Load client's own configuration
        if (data.clientConfig) {
          setForm({
            clientId: data.clientConfig.clientId || routeClientId,
            companyName: data.clientConfig.companyName || '',
            agentId: data.clientConfig.agentId || '',
            primary: data.clientConfig.ui?.colors?.primary || "#bbbbbb",
            secondary: data.clientConfig.ui?.colors?.secondary || "#000000",
            background: data.clientConfig.ui?.background || "#aaaaaa",
            transparentWidgetBg: data.clientConfig.ui?.transparentWidgetBg ?? true,
            bgGradient: data.clientConfig.ui?.bgGradient ?? false,
            buttonGradient: data.clientConfig.ui?.buttonGradient ?? false,
            cardBg: data.clientConfig.ui?.cardBg || "#e0e0e0",
            cardText: data.clientConfig.ui?.cardText || "#454545",
            buttonBg: data.clientConfig.ui?.buttonBg || "",
            buttonText: data.clientConfig.ui?.buttonText || "#ffffff",
            buttonHover: data.clientConfig.ui?.buttonHover || "",
            transcriptDefaultOpenFull: data.clientConfig.ui?.transcriptDefaultOpenFull ?? true,
            transcriptDefaultOpenWidget: data.clientConfig.ui?.transcriptDefaultOpenWidget ?? false,
            transparentBackgroundFull: data.clientConfig.ui?.transparentBackgroundFull ?? false,
            transparentBackgroundWidget: data.clientConfig.ui?.transparentBackgroundWidget ?? true,
            fontFamily: data.clientConfig.ui?.fontFamily || "Montserrat, Arial, sans-serif",
            fontSize: data.clientConfig.ui?.fontSize || "16px",
            iconType: data.clientConfig.ui?.icon?.type || "mic",
            iconColor: data.clientConfig.ui?.icon?.color || "#ffffff",
            avatarType: data.clientConfig.ui?.avatar || "avatar.png",
            showRoleIcons: data.clientConfig.ui?.showRoleIcons ?? false,
            minSize: data.clientConfig.ui?.layout?.widget?.min || 120,
            defaultSize: data.clientConfig.ui?.layout?.widget?.size || 160,
            maxSize: data.clientConfig.ui?.layout?.widget?.max || 190,
            layout: data.clientConfig.ui?.layout || {
              widget: { mode: 'responsive', size: 160, min: 120, max: 190 },
              full: { mode: 'responsive', size: 160, min: 120, max: 190 },
            },
            showGlow: data.clientConfig.ui?.showGlow ?? true,
            showWaves: data.clientConfig.ui?.showWaves ?? true,
            fullPageWidth: data.clientConfig.ui?.fullPageWidth || 800,
            fullPageHeight: data.clientConfig.ui?.fullPageHeight || 'auto',
            fullPageHeadline: data.clientConfig.ui?.fullPageHeadline || "Get your (Offer Here)!",
            fullPageSubheadline: data.clientConfig.ui?.fullPageSubheadline || "Click to talk to our AI Assistant",
            fullPageFooter: data.clientConfig.ui?.fullPageFooter || "Â© 2025 Powered by Sellflows.com -- All rights reserved.",
            headlineFontSize: data.clientConfig.ui?.headlineFontSize || 28,
            subheadlineFontSize: data.clientConfig.ui?.subheadlineFontSize || 18,
            headlineColor: data.clientConfig.ui?.headlineColor || "primary",
            headlineCustomColor: data.clientConfig.ui?.headlineCustomColor || "#6ec6ff",
            subheadlineColor: data.clientConfig.ui?.subheadlineColor || "secondary",
            subheadlineCustomColor: data.clientConfig.ui?.subheadlineCustomColor || "#ff6ec4",
            footerColor: data.clientConfig.ui?.footerColor || "secondary",
            footerCustomColor: data.clientConfig.ui?.footerCustomColor || "#ffffff",
            backgroundFull: data.clientConfig.ui?.backgroundFull || "",
            primaryFull: data.clientConfig.ui?.colors?.primary || "",
            secondaryFull: data.clientConfig.ui?.colors?.secondary || "",
            buttonBgFull: data.clientConfig.ui?.buttonBg || "",
            buttonTextFull: data.clientConfig.ui?.buttonText || "#ffffff",
            cardBgFull: data.clientConfig.ui?.cardBg || "#e0e0e0",
            cardTextFull: data.clientConfig.ui?.cardText || "#454545",
            backgroundFullType: data.clientConfig.ui?.backgroundFullType || "primary",
            buttonBgFullType: data.clientConfig.ui?.buttonBgFullType || "primary",
            buttonTextFullType: data.clientConfig.ui?.buttonTextFullType || "primary",
            cardBgFullType: data.clientConfig.ui?.cardBgFullType || "primary",
            cardTextFullType: data.clientConfig.ui?.cardTextFullType || "primary",
            backgroundFullCustom: data.clientConfig.ui?.backgroundFullCustom || "",
            buttonBgFullCustom: data.clientConfig.ui?.buttonBgFullCustom || "",
            buttonTextFullCustom: data.clientConfig.ui?.buttonTextFullCustom || "",
            cardBgFullCustom: data.clientConfig.ui?.cardBgFullCustom || "",
            cardTextFullCustom: data.clientConfig.ui?.cardTextFullCustom || "",
            transcriptFontSize: 14,
          });
        }
      } else if (response.ok && data.needsPasswordSetup) {
        // Show password setup form
        setShowPasswordSetup(true);
        setLoginError('');
        // Load client configuration for the setup form
        if (data.clientConfig) {
          setForm({
            clientId: data.clientConfig.clientId || routeClientId,
            companyName: data.clientConfig.companyName || '',
            agentId: data.clientConfig.agentId || '',
            primary: data.clientConfig.ui?.colors?.primary || "#bbbbbb",
            secondary: data.clientConfig.ui?.colors?.secondary || "#000000",
            background: data.clientConfig.ui?.background || "#aaaaaa",
            transparentWidgetBg: data.clientConfig.ui?.transparentWidgetBg ?? true,
            bgGradient: data.clientConfig.ui?.bgGradient ?? false,
            buttonGradient: data.clientConfig.ui?.buttonGradient ?? false,
            cardBg: data.clientConfig.ui?.cardBg || "#e0e0e0",
            cardText: data.clientConfig.ui?.cardText || "#454545",
            buttonBg: data.clientConfig.ui?.buttonBg || "",
            buttonText: data.clientConfig.ui?.buttonText || "#ffffff",
            buttonHover: data.clientConfig.ui?.buttonHover || "",
            transcriptDefaultOpenFull: data.clientConfig.ui?.transcriptDefaultOpenFull ?? true,
            transcriptDefaultOpenWidget: data.clientConfig.ui?.transcriptDefaultOpenWidget ?? false,
            transparentBackgroundFull: data.clientConfig.ui?.transparentBackgroundFull ?? false,
            transparentBackgroundWidget: data.clientConfig.ui?.transparentBackgroundWidget ?? true,
            fontFamily: data.clientConfig.ui?.fontFamily || "Montserrat, Arial, sans-serif",
            fontSize: data.clientConfig.ui?.fontSize || "16px",
            iconType: data.clientConfig.ui?.icon?.type || "mic",
            iconColor: data.clientConfig.ui?.icon?.color || "#ffffff",
            avatarType: data.clientConfig.ui?.avatar || "avatar.png",
            showRoleIcons: data.clientConfig.ui?.showRoleIcons ?? false,
            minSize: data.clientConfig.ui?.layout?.widget?.min || 120,
            defaultSize: data.clientConfig.ui?.layout?.widget?.size || 160,
            maxSize: data.clientConfig.ui?.layout?.widget?.max || 190,
            layout: data.clientConfig.ui?.layout || {
              widget: { mode: 'responsive', size: 160, min: 120, max: 190 },
              full: { mode: 'responsive', size: 160, min: 120, max: 190 },
            },
            showGlow: data.clientConfig.ui?.showGlow ?? true,
            showWaves: data.clientConfig.ui?.showWaves ?? true,
            fullPageWidth: data.clientConfig.ui?.fullPageWidth || 800,
            fullPageHeight: data.clientConfig.ui?.fullPageHeight || 'auto',
            fullPageHeadline: data.clientConfig.ui?.fullPageHeadline || "Get your (Offer Here)!",
            fullPageSubheadline: data.clientConfig.ui?.fullPageSubheadline || "Click to talk to our AI Assistant",
            fullPageFooter: data.clientConfig.ui?.fullPageFooter || "Â© 2025 Powered by Sellflows.com -- All rights reserved.",
            headlineFontSize: data.clientConfig.ui?.headlineFontSize || 28,
            subheadlineFontSize: data.clientConfig.ui?.subheadlineFontSize || 18,
            headlineColor: data.clientConfig.ui?.headlineColor || "primary",
            headlineCustomColor: data.clientConfig.ui?.headlineCustomColor || "#6ec6ff",
            subheadlineColor: data.clientConfig.ui?.subheadlineColor || "secondary",
            subheadlineCustomColor: data.clientConfig.ui?.subheadlineCustomColor || "#ff6ec4",
            footerColor: data.clientConfig.ui?.footerColor || "secondary",
            footerCustomColor: data.clientConfig.ui?.footerCustomColor || "#ffffff",
            backgroundFull: data.clientConfig.ui?.backgroundFull || "",
            primaryFull: data.clientConfig.ui?.colors?.primary || "",
            secondaryFull: data.clientConfig.ui?.colors?.secondary || "",
            buttonBgFull: data.clientConfig.ui?.buttonBg || "",
            buttonTextFull: data.clientConfig.ui?.buttonText || "#ffffff",
            cardBgFull: data.clientConfig.ui?.cardBg || "#e0e0e0",
            cardTextFull: data.clientConfig.ui?.cardText || "#454545",
            backgroundFullType: data.clientConfig.ui?.backgroundFullType || "primary",
            buttonBgFullType: data.clientConfig.ui?.buttonBgFullType || "primary",
            buttonTextFullType: data.clientConfig.ui?.buttonTextFullType || "primary",
            cardBgFullType: data.clientConfig.ui?.cardBgFullType || "primary",
            cardTextFullType: data.clientConfig.ui?.cardTextFullType || "primary",
            backgroundFullCustom: data.clientConfig.ui?.backgroundFullCustom || "",
            buttonBgFullCustom: data.clientConfig.ui?.buttonBgFullCustom || "",
            buttonTextFullCustom: data.clientConfig.ui?.buttonTextFullCustom || "",
            cardBgFullCustom: data.clientConfig.ui?.cardBgFullCustom || "",
            cardTextFullCustom: data.clientConfig.ui?.cardTextFullCustom || "",
            transcriptFontSize: 14,
          });
        }
      } else {
        setLoginError(data.message || 'Login failed');
      }
    } catch (error) {
      setLoginError('Login failed. Please try again.');
    }
  };



  const handlePasswordReset = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE}/client-reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });
      
      if (response.ok) {
        setShowPasswordReset(false);
        setResetEmail('');
        setSuccess('Password reset email sent!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setLoginError(data.message || 'Failed to send reset email');
      }
    } catch (error) {
      setLoginError('Network error. Please try again.');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/client-change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          clientId: routeClientId, 
          currentPassword, 
          newPassword 
        })
      });
      
      if (response.ok) {
        setShowChangePassword(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordSuccess('Password changed successfully!');
        setTimeout(() => setPasswordSuccess(''), 3000);
      } else {
        const data = await response.json();
        setPasswordError(data.message || 'Failed to change password');
      }
    } catch (error) {
      setPasswordError('Network error. Please try again.');
    }
  };

  const handlePasswordSetup = async (e) => {
    e.preventDefault();
    setSetupError('');
    
    if (!setupPassword.trim()) {
      setSetupError('Please enter a password');
      return;
    }
    
    if (setupPassword !== confirmSetupPassword) {
      setSetupError('Passwords do not match');
      return;
    }
    
    if (setupPassword.length < 6) {
      setSetupError('Password must be at least 6 characters');
      return;
    }

    try {
      console.log('Sending password setup request for client:', routeClientId);
      const response = await fetch(`${API_BASE}/client-setup-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify({
          clientId: routeClientId,
          password: setupPassword
        }),
      });

      console.log('Password setup response status:', response.status);
      const data = await response.json();
      console.log('Password setup response data:', data);

      if (response.ok && data.success) {
        console.log('Password setup successful, redirecting to edit tab...');
        setClientAuthenticated(true);
        setShowPasswordSetup(false);
        handleTabChange('edit');
        console.log('State changes applied - clientAuthenticated: true, showPasswordSetup: false, activeTab: edit');
        // Load client's own configuration
        if (data.clientConfig) {
          console.log('Loading client config after password setup:', data.clientConfig);
          setForm({
            clientId: data.clientConfig.clientId || routeClientId,
            companyName: data.clientConfig.companyName || '',
            agentId: data.clientConfig.agentId || '',
            primary: data.clientConfig.ui?.colors?.primary || "#bbbbbb",
            secondary: data.clientConfig.ui?.colors?.secondary || "#000000",
            background: data.clientConfig.ui?.background || "#aaaaaa",
            transparentWidgetBg: data.clientConfig.ui?.transparentWidgetBg ?? true,
            bgGradient: data.clientConfig.ui?.bgGradient ?? false,
            buttonGradient: data.clientConfig.ui?.buttonGradient ?? false,
            cardBg: data.clientConfig.ui?.cardBg || "#e0e0e0",
            cardText: data.clientConfig.ui?.cardText || "#454545",
            buttonBg: data.clientConfig.ui?.buttonBg || "",
            buttonText: data.clientConfig.ui?.buttonText || "#ffffff",
            buttonHover: data.clientConfig.ui?.buttonHover || "",
            transcriptDefaultOpenFull: data.clientConfig.ui?.transcriptDefaultOpenFull ?? true,
            transcriptDefaultOpenWidget: data.clientConfig.ui?.transcriptDefaultOpenWidget ?? false,
            transparentBackgroundFull: data.clientConfig.ui?.transparentBackgroundFull ?? false,
            transparentBackgroundWidget: data.clientConfig.ui?.transparentBackgroundWidget ?? true,
            fontFamily: data.clientConfig.ui?.fontFamily || "Montserrat, Arial, sans-serif",
            fontSize: data.clientConfig.ui?.fontSize || "16px",
            iconType: data.clientConfig.ui?.icon?.type || "mic",
            iconColor: data.clientConfig.ui?.icon?.color || "#ffffff",
            avatarType: data.clientConfig.ui?.avatar || "avatar.png",
            showRoleIcons: data.clientConfig.ui?.showRoleIcons ?? false,
            minSize: data.clientConfig.ui?.layout?.widget?.min || 120,
            defaultSize: data.clientConfig.ui?.layout?.widget?.size || 160,
            maxSize: data.clientConfig.ui?.layout?.widget?.max || 190,
            layout: data.clientConfig.ui?.layout || {
              widget: { mode: 'responsive', size: 160, min: 120, max: 190 },
              full: { mode: 'responsive', size: 160, min: 120, max: 190 },
            },
            showGlow: data.clientConfig.ui?.showGlow ?? true,
            showWaves: data.clientConfig.ui?.showWaves ?? true,
            fullPageWidth: data.clientConfig.ui?.fullPageWidth || 800,
            fullPageHeight: data.clientConfig.ui?.fullPageHeight || 'auto',
            fullPageHeadline: data.clientConfig.ui?.fullPageHeadline || "Get your (Offer Here)!",
            fullPageSubheadline: data.clientConfig.ui?.fullPageSubheadline || "Click to talk to our AI Assistant",
            fullPageFooter: data.clientConfig.ui?.fullPageFooter || "Â© 2025 Powered by Sellflows.com -- All rights reserved.",
            headlineFontSize: data.clientConfig.ui?.headlineFontSize || 28,
            subheadlineFontSize: data.clientConfig.ui?.subheadlineFontSize || 18,
            headlineColor: data.clientConfig.ui?.headlineColor || "primary",
            headlineCustomColor: data.clientConfig.ui?.headlineCustomColor || "#6ec6ff",
            subheadlineColor: data.clientConfig.ui?.subheadlineColor || "secondary",
            subheadlineCustomColor: data.clientConfig.ui?.subheadlineCustomColor || "#ff6ec4",
            footerColor: data.clientConfig.ui?.footerColor || "secondary",
            footerCustomColor: data.clientConfig.ui?.footerCustomColor || "#ffffff",
            backgroundFull: data.clientConfig.ui?.backgroundFull || "",
            primaryFull: data.clientConfig.ui?.colors?.primary || "",
            secondaryFull: data.clientConfig.ui?.colors?.secondary || "",
            buttonBgFull: data.clientConfig.ui?.buttonBg || "",
            buttonTextFull: data.clientConfig.ui?.buttonText || "#ffffff",
            cardBgFull: data.clientConfig.ui?.cardBg || "#e0e0e0",
            cardTextFull: data.clientConfig.ui?.cardText || "#454545",
            backgroundFullType: data.clientConfig.ui?.backgroundFullType || "primary",
            buttonBgFullType: data.clientConfig.ui?.buttonBgFullType || "primary",
            buttonTextFullType: data.clientConfig.ui?.buttonTextFullType || "primary",
            cardBgFullType: data.clientConfig.ui?.cardBgFullType || "primary",
            cardTextFullType: data.clientConfig.ui?.cardTextFullType || "primary",
            backgroundFullCustom: data.clientConfig.ui?.backgroundFullCustom || "",
            buttonBgFullCustom: data.clientConfig.ui?.buttonBgFullCustom || "",
            buttonTextFullCustom: data.clientConfig.ui?.buttonTextFullCustom || "",
            cardBgFullCustom: data.clientConfig.ui?.cardBgFullCustom || "",
            cardTextFullCustom: data.clientConfig.ui?.cardTextFullCustom || "",
            transcriptFontSize: 14,
          });
        }
      } else {
        setSetupError(data.message || 'Password setup failed');
      }
    } catch (error) {
      console.error('Password setup error:', error);
      setSetupError('Password setup failed');
    }
  };

  const handleGeneratePasswordLink = async (clientId) => {
    try {
      const response = await fetch(`${API_BASE}/generate-password-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify({ clientId })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Copy link to clipboard
        await navigator.clipboard.writeText(data.link);
        setSuccess(`${data.message} - Link copied to clipboard!`);
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.message || 'Failed to generate password link');
      }
    } catch (error) {
      console.error('Error generating password link:', error);
      setError('Failed to generate password link');
    }
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    const effectiveClientId = resetClientId || routeClientId || '';
    
    if (!effectiveClientId.trim()) {
      setResetError('Client ID is missing. Please request a new reset link.');
      return;
    }

    if (!resetNewPassword.trim()) {
      setResetError('Please enter a new password');
      return;
    }
    
    if (resetNewPassword !== resetConfirmPassword) {
      setResetError('Passwords do not match');
      return;
    }
    
    if (resetNewPassword.length < 6) {
      setResetError('Password must be at least 6 characters long');
      return;
    }
    
    try {
      console.log('Sending reset request:', {
        clientId: effectiveClientId,
        newPassword: resetNewPassword
      });
      
      const response = await fetch(`${API_BASE}/client-reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: effectiveClientId,
          newPassword: resetNewPassword
        })
      });
      
      let data = null;
      try {
        data = await response.json();
      } catch {
        // Non-JSON response (proxy/HTML error page) - keep data as null
      }
      
      if (response.ok) {
        setIsResetMode(false);
        setResetNewPassword('');
        setResetConfirmPassword('');
        setResetSuccess('Password reset successfully! You can now log in with your new password.');
        setTimeout(() => setResetSuccess(''), 5000);
      } else {
        setResetError(data?.message || `Failed to reset password (HTTP ${response.status})`);
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setResetError('Password reset failed');
    }
  };


  console.log('isAuthed state:', isAuthed);
  
  const handleLogout = () => {
    localStorage.removeItem('adminKey');
    setIsAuthed(false);
    setAdminKey('');
  };

  // Handle tab change
  const handleTabChange = (newTab) => {
    if (isClientView && newTab !== 'edit') {
      setActiveTab('edit');
      return;
    }
    // Check if there are unsaved changes when switching from edit tab
    if (activeTab === 'edit' && newTab !== 'edit' && hasChanges) {
      const shouldSave = window.confirm('You have unsaved changes. Do you want to save before navigating away? Click OK to save, or Cancel to discard changes.');
      if (shouldSave) {
        // Save and then navigate
        handleSubmit().then(() => {
          setActiveTab(newTab);
          setSuccess(""); // Clear success message when navigating
        }).catch(() => {
          // If save fails, don't navigate
          console.log('Save failed, staying on edit tab');
        });
        return; // Don't switch tab yet, let handleSubmit complete
              } else {
          // User chose to discard changes - reset form and switch tab
          if (editId) {
          // For existing client, reload the original data
          const originalClient = clients[editId];
          if (originalClient) {
            setForm({
              clientId: originalClient.clientId || "",
              companyName: originalClient.companyName || "",
              email: originalClient.email || "",
              agentId: originalClient.agentId || "",
              password: originalClient.password || "",
              autoSendPasswordLink: originalClient.autoSendPasswordLink || false,
              primary: originalClient.ui?.colors?.primary || "#3a4f90",
              secondary: originalClient.ui?.colors?.secondary || "#19647e",
              background: originalClient.ui?.colors?.background || "#ffffff",
              transparentWidgetBg: originalClient.ui?.transparentWidgetBg ?? true,
              bgGradient: originalClient.ui?.bgGradient ?? false,
              buttonGradient: originalClient.ui?.buttonGradient ?? false,
              cardBg: originalClient.ui?.colors?.cardBg || "#e0e0e0",
              cardText: originalClient.ui?.colors?.cardText || "#454545",
              buttonBg: originalClient.ui?.colors?.buttonBg || "",
              buttonText: originalClient.ui?.colors?.buttonText || "#ffffff",
              buttonHover: originalClient.ui?.colors?.buttonHover || "",
              transcriptDefaultOpenFull: originalClient.ui?.transcriptDefaultOpenFull ?? true,
              transcriptDefaultOpenWidget: originalClient.ui?.transcriptDefaultOpenWidget ?? false,
              transparentBackgroundFull: originalClient.ui?.colors?.backgroundFull === "transparent",
              transparentBackgroundWidget: originalClient.ui?.colors?.backgroundWidget === "transparent",
              fontFamily: originalClient.ui?.fonts?.family || "Montserrat, Arial, sans-serif",
              fontSize: originalClient.ui?.fonts?.size || "14",
              iconType: originalClient.ui?.icon?.type || "classic-mic",
              iconColor: originalClient.ui?.icon?.color || "#ffffff",
              avatarType: originalClient.ui?.avatar || "avatar.png",
              showRoleIcons: originalClient.ui?.showRoleIcons ?? false,
              minSize: originalClient.ui?.layout?.minSize || 120,
              defaultSize: originalClient.ui?.layout?.defaultSize || 160,
              maxSize: originalClient.ui?.layout?.maxSize || 190,
              layout: {
                widget: originalClient.ui?.layout?.widget || { mode: 'fixed', size: 160, min: 120, max: 190 },
                full: originalClient.ui?.layout?.full || { mode: 'fixed', size: 160, min: 120, max: 190 },
              },
              showGlow: originalClient.ui?.showGlow ?? true,
              showWaves: originalClient.ui?.showWaves ?? true,
              fullPageWidth: originalClient.ui?.fullPageWidth || 800,
              fullPageHeight: originalClient.ui?.fullPageHeight || 'auto',
              fullPageHeadline: originalClient.ui?.fullPageHeadline || "Get your (Offer Here)!",
              fullPageSubheadline: originalClient.ui?.fullPageSubheadline || "Click to talk to our AI Assistant",
              fullPageFooter: originalClient.ui?.fullPageFooter || "Â© 2025 Powered by Sellflows.com -- All rights reserved.",
              headlineFontSize: originalClient.ui?.headlineFontSize || 28,
              subheadlineFontSize: originalClient.ui?.subheadlineFontSize || 18,
              headlineColor: originalClient.ui?.headlineColor || "primary",
              headlineCustomColor: originalClient.ui?.headlineCustomColor || "#6ec6ff",
              subheadlineColor: originalClient.ui?.subheadlineColor || "secondary",
              subheadlineCustomColor: originalClient.ui?.subheadlineCustomColor || "#ff6ec4",
              footerColor: originalClient.ui?.footerColor || "secondary",
              footerCustomColor: originalClient.ui?.footerCustomColor || "#ffffff",
              backgroundFull: originalClient.ui?.backgroundFull || "",
              primaryFull: originalClient.ui?.primaryFull || "",
              secondaryFull: originalClient.ui?.secondaryFull || "",
              buttonBgFull: originalClient.ui?.buttonBgFull || "",
              buttonTextFull: originalClient.ui?.buttonTextFull || "#ffffff",
              cardBgFull: originalClient.ui?.cardBgFull || "",
              cardTextFull: originalClient.ui?.cardTextFull || "",
              hideSensitiveFields: originalClient.hideSensitiveFields ?? true,
              backgroundFullType: originalClient.ui?.backgroundFullType || "primary",
              buttonBgFullType: originalClient.ui?.buttonBgFullType || "primary",
              buttonTextFullType: originalClient.ui?.buttonTextFullType || "primary",
              cardBgFullType: originalClient.ui?.cardBgFullType || "primary",
              cardTextFullType: originalClient.ui?.cardTextFullType || "primary",
              backgroundFullCustom: originalClient.ui?.backgroundFullCustom || "",
              buttonBgFullCustom: originalClient.ui?.buttonBgFullCustom || "",
              buttonTextFullCustom: originalClient.ui?.buttonTextFullCustom || "",
              cardBgFullCustom: originalClient.ui?.cardBgFullCustom || "",
              cardTextFullCustom: originalClient.ui?.cardTextFullCustom || "",
              transcriptFontSize: originalClient.ui?.transcriptFontSize || 14,
            });
            setHasChanges(false); // Reset change tracking after discarding changes
          }
        } else {
          // For new client, reset to empty form and clear editId
          if (newTab !== 'edit') {
            setEditId(null); // This will make the tab show "Add Client"
          }
          setHasChanges(false); // Reset change tracking for new client
          setForm({
            clientId: "",
            companyName: "",
            email: "",
            agentId: "",
            primary: "#bbbbbb",
            secondary: "#000000",
            background: "#ffffff",
            transparentWidgetBg: true,
            bgGradient: false,
            buttonGradient: false,
            cardBg: "#e0e0e0",
            cardText: "#454545",
            buttonBg: "",
            buttonText: "#ffffff",
            buttonHover: "",
            transcriptDefaultOpenFull: true,
            transcriptDefaultOpenWidget: false,
            transparentBackgroundFull: false,
            transparentBackgroundWidget: true,
            fontFamily: "Montserrat, Arial, sans-serif",
            fontSize: "14",
            iconType: "classic-mic",
            iconColor: "#ffffff",
            avatarType: "avatar.png",
            showRoleIcons: false,
            minSize: 120,
            defaultSize: 160,
            maxSize: 190,
            layout: {
              widget: { mode: 'fixed', size: 160, min: 120, max: 190 },
              full: { mode: 'fixed', size: 160, min: 120, max: 190 },
            },
            showGlow: true,
            showWaves: true,
            fullPageWidth: 800,
            fullPageHeight: 'auto',
            fullPageHeadline: "Get your (Offer Here)!",
            fullPageSubheadline: "Click to talk to our AI Assistant",
            fullPageFooter: "Â© 2025 Powered by Sellflows.com -- All rights reserved.",
            headlineFontSize: 28,
            subheadlineFontSize: 18,
            headlineColor: "primary",
            headlineCustomColor: "#6ec6ff",
            subheadlineColor: "secondary",
            subheadlineCustomColor: "#ff6ec4",
            footerColor: "secondary",
            footerCustomColor: "#ffffff",
            backgroundFull: "",
            
            buttonBgFull: "",
            buttonTextFull: "#ffffff",
            cardBgFull: "",
            cardTextFull: "",
            password: "",
            hideSensitiveFields: true,
            autoSendPasswordLink: false,
            backgroundFullType: "primary",
            buttonBgFullType: "primary",
            buttonTextFullType: "primary",
            cardBgFullType: "primary",
            cardTextFullType: "primary",
            backgroundFullCustom: "",
            buttonBgFullCustom: "",
            buttonTextFullCustom: "",
            cardBgFullCustom: "",
            cardTextFullCustom: "",
            transcriptFontSize: 14,
          });
        }
        setSuccess(""); // Clear success message
        setError(""); // Clear error message
        setEditId(null); // Clear editId to show "Add Client" tab title
        setActiveTab(newTab);
        return;
      }
    }
    // Clear messages when switching tabs normally
    setSuccess("");
    setError("");
    // Only clear editId if NOT switching to edit tab
    if (newTab !== 'edit') {
      setEditId(null); // Clear editId to show "Add Client" tab title when navigating normally
    }
    setActiveTab(newTab);
  };

  // Function to check if there are unsaved changes
  // Simple change tracking system
  
  // Track when form changes (not used anymore, replaced by handleChange)
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setForm(prev => {
      // Handle nested properties like "layout.full.mode"
      if (name.includes('.')) {
        const parts = name.split('.');
        const newForm = { ...prev };
        let current = newForm;
        
        // Navigate to the nested object
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          }
          current = current[parts[i]];
        }
        
        // Set the final property
        current[parts[parts.length - 1]] = newValue;
        return newForm;
      } else {
        // Handle simple properties
        return {
          ...prev,
          [name]: newValue
        };
      }
    });
    
    // Mark as having changes
    setHasChanges(true);
  };

  // File management functions for Settings tab
  const handleSettingsAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append('avatar', file);
    
    try {
      const response = await fetch(`${API_BASE}/upload/avatar`, {
        method: 'POST',
        headers: {
          'x-admin-key': adminKey
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccess('Avatar uploaded successfully!');
        // Auto-clear success message after 5 seconds
        setTimeout(() => setSuccess(""), 5000);
        fetchAvatars(); // Refresh the list
      } else {
        setError('Failed to upload avatar');
      }
    } catch (error) {
      setError('Error uploading avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSettingsSvgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingSVG(true);
    const formData = new FormData();
    formData.append('svg', file);
    
    try {
      const response = await fetch(`${API_BASE}/upload/svg`, {
        method: 'POST',
        headers: {
          'x-admin-key': adminKey
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccess('SVG uploaded successfully!');
        // Auto-clear success message after 5 seconds
        setTimeout(() => setSuccess(""), 5000);
        fetchSVGs(); // Refresh the list
      } else {
        setError('Failed to upload SVG');
      }
    } catch (error) {
      setError('Error uploading SVG');
    } finally {
      setUploadingSVG(false);
    }
  };

  const handleDeleteFile = async (filename, fileType) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) return;
    
    setDeletingFile(true);
    try {
      console.log(`Attempting to delete ${fileType}: ${filename}`);
      const response = await fetch(`${API_BASE}/delete/${fileType}/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: {
          'x-admin-key': adminKey
        }
      });
      
      console.log(`Delete response status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Delete successful:', data);
        setSuccess(`${fileType === 'avatar' ? 'Avatar' : 'SVG'} deleted successfully!`);
        setTimeout(() => setSuccess(''), 3000);
        
        // Force refresh the file lists
        if (fileType === 'avatar') {
          await fetchAvatars();
        } else {
          await fetchSVGs();
        }
        
        // Also refresh both lists to ensure consistency
        setTimeout(() => {
          fetchAvatars();
          fetchSVGs();
        }, 100);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Delete failed:', errorData);
        setError(`Failed to delete ${fileType}: ${errorData.error || 'Unknown error'}`);
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error(`Error deleting ${fileType}:`, error);
      setError(`Error deleting ${fileType}: ${error.message}`);
      setTimeout(() => setError(''), 3000);
    } finally {
      setDeletingFile(false);
    }
  };

  return (
    <div style={{ padding: 16, background: "#f5f5f5", minHeight: "100vh" }}>
      <style>
        {`
          @keyframes adminRotateGlow {
            100% { transform: translate(-50%, -50%) rotate(360deg); }
          }
        `}
      </style>
      {/* Reset Password Interface - Show when reset mode is active */}
      {isResetMode && (
        <div style={{ 
          maxWidth: 400, 
          margin: "20px auto", 
          padding: 24, 
          background: "#fff", 
          borderRadius: 12, 
          boxShadow: "0 2px 16px #0002",
          textAlign: 'center'
        }}>
          <h2 style={{ marginBottom: 20, color: '#3a4f90' }}>
            Reset Password
          </h2>
          
          {resetSuccess ? (
            <div style={{ color: 'green', marginBottom: 16, fontSize: '0.9em' }}>
              {resetSuccess}
            </div>
          ) : (
            <form onSubmit={handleResetPassword}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, textAlign: 'left', fontWeight: '500' }}>
                  Client ID:
                </label>
                <input
                  type="text"
                  value={resetClientId || ''}
                  readOnly
                  placeholder="Client ID from URL"
                  style={{ 
                    width: '100%', 
                    padding: 12, 
                    borderRadius: 6, 
                    border: "1px solid #ccc", 
                    fontSize: "0.95em",
                    backgroundColor: '#f5f5f5',
                    color: '#666'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, textAlign: 'left', fontWeight: '500' }}>
                  New Password:
                </label>
                <input
                  type="password"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  style={{ 
                    width: '100%', 
                    padding: 12, 
                    borderRadius: 6, 
                    border: "1px solid #ccc", 
                    fontSize: "0.95em" 
                  }}
                />
              </div>
              
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, textAlign: 'left', fontWeight: '500' }}>
                  Confirm Password:
                </label>
                <input
                  type="password"
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  style={{ 
                    width: '100%', 
                    padding: 12, 
                    borderRadius: 6, 
                    border: "1px solid #ccc", 
                    fontSize: "0.95em" 
                  }}
                />
              </div>
              
              {resetError && (
                <div style={{ color: 'red', marginBottom: 16, fontSize: '0.9em' }}>
                  {resetError}
                </div>
              )}
              
              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: 12,
                  background: '#3a4f90',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: '1em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  marginBottom: 12
                }}
              >
                Reset Password
              </button>
              
              <button
                type="button"
                onClick={() => setIsResetMode(false)}
                style={{
                  width: '100%',
                  padding: 8,
                  background: 'transparent',
                  color: '#3a4f90',
                  border: '1px solid #3a4f90',
                  borderRadius: 6,
                  fontSize: '0.9em',
                  cursor: 'pointer'
                }}
              >
                Back to Login
              </button>
            </form>
          )}
        </div>
      )}
      
      {/* Client Login Interface - Only show when not authenticated and in client mode */}
      {!clientAuthenticated && isClientMode && !showPasswordSetup && !isResetMode && (
        <div style={{ 
          maxWidth: 400, 
          margin: "20px auto", 
          padding: 24, 
          background: "#fff", 
          borderRadius: 12, 
          boxShadow: "0 2px 16px #0002",
          textAlign: 'center'
        }}>
          <h2 style={{ marginBottom: 20, color: '#3a4f90' }}>
            {isClientMode ? 'Client Login' : 'Access Your Widget'}
          </h2>
          
          {!showPasswordReset ? (
            <form onSubmit={handleClientLogin}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, textAlign: 'left', fontWeight: '500' }}>
                  Email or Client ID:
                </label>
                <input
                  type="text"
                  value={clientIdentifier}
                  onChange={(e) => setClientIdentifier(e.target.value)}
                  placeholder="Enter email or Client ID"
                  style={{ 
                    width: '100%', 
                    padding: 12, 
                    borderRadius: 6, 
                    border: "1px solid #ccc", 
                    fontSize: "0.95em"
                  }}
                />
              </div>
              
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, textAlign: 'left', fontWeight: '500' }}>
                  Password:
                </label>
                <input
                  type="password"
                  value={clientPassword}
                  onChange={(e) => setClientPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{ 
                    width: '100%', 
                    padding: 12, 
                    borderRadius: 6, 
                    border: "1px solid #ccc", 
                    fontSize: "0.95em" 
                  }}
                />
              </div>
              
              {loginError && (
                <div style={{ color: 'red', marginBottom: 16, fontSize: '0.9em' }}>
                  {loginError}
                </div>
              )}
              
              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: 12,
                  background: '#3a4f90',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: '1em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  marginBottom: 12
                }}
              >
                Login
              </button>
              
              <button
                type="button"
                onClick={() => setShowPasswordReset(true)}
                style={{
                  width: '100%',
                  padding: 8,
                  background: 'transparent',
                  color: '#3a4f90',
                  border: '1px solid #3a4f90',
                  borderRadius: 6,
                  fontSize: '0.9em',
                  cursor: 'pointer'
                }}
              >
                Forgot Password?
              </button>
              
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #eee' }}>
                <button
                  type="button"
                  onClick={() => navigate('/admin')}
                  style={{
                    background: 'transparent',
                    color: '#666',
                    border: 'none',
                    fontSize: '0.9em',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Back to Admin Panel
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handlePasswordReset}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, textAlign: 'left', fontWeight: '500' }}>
                  Email Address:
                </label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="Enter your email address"
                  style={{ 
                    width: '100%', 
                    padding: 12, 
                    borderRadius: 6, 
                    border: "1px solid #ccc", 
                    fontSize: "0.95em" 
                  }}
                />
              </div>
              
              {loginError && (
                <div style={{ color: 'red', marginBottom: 16, fontSize: '0.9em' }}>
                  {loginError}
                </div>
              )}
              
              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: 12,
                  background: '#3a4f90',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: '1em',
                  fontWeight: '500',
                  cursor: 'pointer',
                  marginBottom: 12
                }}
              >
                Send Reset Email
              </button>
              
              <button
                type="button"
                onClick={() => setShowPasswordReset(false)}
                style={{
                  width: '100%',
                  padding: 8,
                  background: 'transparent',
                  color: '#666',
                  border: '1px solid #ccc',
                  borderRadius: 6,
                  fontSize: '0.9em',
                  cursor: 'pointer'
                }}
              >
                Back to Login
              </button>
            </form>
          )}
        </div>
      )}
      
      {/* Password Setup Interface - Show when password setup is needed */}
      {showPasswordSetup && (
        <div style={{ 
          maxWidth: 400, 
          margin: "20px auto", 
          padding: 24, 
          background: "#fff", 
          borderRadius: 12, 
          boxShadow: "0 2px 16px #0002",
          textAlign: 'center'
        }}>
          <h2 style={{ marginBottom: 20, color: '#3a4f90' }}>
            Set Your Password
          </h2>
          <p style={{ marginBottom: 20, color: '#666', fontSize: '0.9em' }}>
            Welcome! Please set a password for your account.
          </p>
          
          <form onSubmit={handlePasswordSetup}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, textAlign: 'left', fontWeight: '500' }}>
                New Password:
              </label>
              <input
                type="password"
                value={setupPassword}
                onChange={(e) => setSetupPassword(e.target.value)}
                placeholder="Enter your new password"
                style={{ 
                  width: '100%', 
                  padding: 12, 
                  borderRadius: 6, 
                  border: "1px solid #ccc", 
                  fontSize: "0.95em" 
                }}
              />
            </div>
            
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, textAlign: 'left', fontWeight: '500' }}>
                Confirm Password:
              </label>
              <input
                type="password"
                value={confirmSetupPassword}
                onChange={(e) => setConfirmSetupPassword(e.target.value)}
                placeholder="Confirm your new password"
                style={{ 
                  width: '100%', 
                  padding: 12, 
                  borderRadius: 6, 
                  border: "1px solid #ccc", 
                  fontSize: "0.95em" 
                }}
              />
            </div>
            
            {setupError && (
              <div style={{ color: 'red', marginBottom: 16, fontSize: '0.9em' }}>
                {setupError}
              </div>
            )}
            
            <button
              type="submit"
              style={{
                width: '100%',
                padding: 12,
                background: '#3a4f90',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: '1em',
                fontWeight: '500',
                cursor: 'pointer',
                marginBottom: 12
              }}
            >
              Set Password
            </button>
          </form>
        </div>
      )}
      
      {/* Change Password Dialog */}
      {showChangePassword && (
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ 
            background: '#fff', 
            padding: '24px', 
            borderRadius: '12px', 
            maxWidth: '400px', 
            width: '90%',
            textAlign: 'center'
          }}>
            <h3 style={{ marginBottom: '20px', color: '#3a4f90' }}>Change Password</h3>
            
            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, textAlign: 'left', fontWeight: '500' }}>
                  Current Password:
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  style={{ 
                    width: '100%', 
                    padding: 12, 
                    borderRadius: 6, 
                    border: "1px solid #ccc", 
                    fontSize: "0.95em" 
                  }}
                  required
                />
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, textAlign: 'left', fontWeight: '500' }}>
                  New Password:
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  style={{ 
                    width: '100%', 
                    padding: 12, 
                    borderRadius: 6, 
                    border: "1px solid #ccc", 
                    fontSize: "0.95em" 
                  }}
                  required
                />
              </div>
              
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, textAlign: 'left', fontWeight: '500' }}>
                  Confirm New Password:
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  style={{ 
                    width: '100%', 
                    padding: 12, 
                    borderRadius: 6, 
                    border: "1px solid #ccc", 
                    fontSize: "0.95em" 
                  }}
                  required
                />
              </div>
              
              {passwordError && (
                <div style={{ color: 'red', marginBottom: 16, fontSize: '0.9em' }}>
                  {passwordError}
                </div>
              )}
              
              {passwordSuccess && (
                <div style={{ color: 'green', marginBottom: 16, fontSize: '0.9em' }}>
                  {passwordSuccess}
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    background: '#3a4f90',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9em'
                  }}
                >
                  Change Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePassword(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9em'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Main editor content: admin mode, or authenticated client mode */}
      {((isClientMode && clientAuthenticated) || !isClientMode) && (
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: 16, background: "#fff", borderRadius: 12, boxShadow: "0 2px 16px #0002", fontSize: "1.1em" }}>
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '8px' }}>
              <img 
                src={settings.logoDark}
                alt={agencyName}
                style={{
                  height: '50px',
                  width: 'auto',
                  objectFit: 'contain'
                }}
              />
              <h1 style={{ fontSize: "1.5em", margin: 0 }}>
                {agencyName} - Voice AI Web Widget Client Admin
              </h1>
            </div>
          </div>
          
          {/* Logout Button */}
          <div style={{ textAlign: 'right', marginBottom: '10px' }}>
            <button
              onClick={handleLogout}
              style={{
                padding: '6px 12px',
                background: '#8b0000',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8em'
              }}
              title="Logout from admin panel"
            >
              Logout
            </button>
          </div>
          
          {/* Tab Navigation */}
          <div style={{ 
            display: 'flex', 
            borderBottom: '2px solid #e0e0e0', 
            marginBottom: '20px',
            backgroundColor: '#f8f9fa'
          }}>
            <button
              hidden={isClientView}
              onClick={() => handleTabChange('clients')}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: activeTab === 'clients' ? '#3a4f90' : 'transparent',
                color: activeTab === 'clients' ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '0.9em',
                fontWeight: activeTab === 'clients' ? '600' : '500',
                borderBottom: activeTab === 'clients' ? '2px solid #3a4f90' : 'none',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={e => activeTab !== 'clients' && (e.target.style.background = '#e9ecef')}
              onMouseOut={e => activeTab !== 'clients' && (e.target.style.background = 'transparent')}
            >
              All Clients
            </button>
            
            <button
              onClick={() => handleTabChange('edit')}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: activeTab === 'edit' ? '#3a4f90' : 'transparent',
                color: activeTab === 'edit' ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '0.9em',
                fontWeight: activeTab === 'edit' ? '600' : '500',
                borderBottom: activeTab === 'edit' ? '2px solid #3a4f90' : 'none',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={e => activeTab !== 'edit' && (e.target.style.background = '#e9ecef')}
              onMouseOut={e => activeTab !== 'edit' && (e.target.style.background = 'transparent')}
            >
              {editId ? `Edit: ${form.companyName || 'Client'}` : 'Add Client'}
            </button>
            
            <button
              hidden={isClientView}
              onClick={() => handleTabChange('settings')}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: activeTab === 'settings' ? '#3a4f90' : 'transparent',
                color: activeTab === 'settings' ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '0.9em',
                fontWeight: activeTab === 'settings' ? '600' : '500',
                borderBottom: activeTab === 'settings' ? '2px solid #3a4f90' : 'none',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={e => activeTab !== 'settings' && (e.target.style.background = '#e9ecef')}
              onMouseOut={e => activeTab !== 'settings' && (e.target.style.background = 'transparent')}
            >
              Settings
            </button>
          </div>
          
        {error && <div style={{ color: "red" }}>{error}</div>}
        {success && <div style={{ color: "green" }}>{success}</div>}
          
          {/* Edit Tab Content */}
          {activeTab === 'edit' && (
            <>
        <form onSubmit={handleSubmit} style={{ marginBottom: 16 }}>
                {editId && (
                  <div style={{ 
                    background: '#f5f5f5', 
                    padding: '12px 16px', 
                    borderRadius: '8px', 
                    marginBottom: '16px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#3a4f90', marginBottom: '4px' }}>
                      {form.companyName || 'Unnamed Company'}
                    </div>
                    <div style={{ fontSize: '0.9em', color: '#666', fontFamily: 'monospace' }}>
                      Client ID: {editId}
                    </div>
                  </div>
                )}
                <h2 style={{ fontSize: "1.1em", marginBottom: 8 }}>{editId ? "Edit Client Settings" : "Add Client"}</h2>
                
                {/* Client Sub-Tab Navigation */}
                <div style={{ 
                  display: 'flex', 
                  borderBottom: '1px solid #e0e0e0', 
                  marginBottom: '20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px 6px 0 0'
                }}>
                  <button
                    type="button"
                    onClick={() => setClientSubTab('general')}
                    style={{
                      flex: '1',
                      padding: '16px 24px',
                      border: 'none',
                      background: clientSubTab === 'general' ? '#3a4f90' : 'transparent',
                      color: clientSubTab === 'general' ? 'white' : '#666',
                      cursor: 'pointer',
                      fontSize: '1em',
                      fontWeight: clientSubTab === 'general' ? '600' : '500',
                      borderBottom: clientSubTab === 'general' ? '3px solid #3a4f90' : 'none',
                      transition: 'all 0.2s ease',
                      borderRadius: '6px 6px 0 0'
                    }}
                    onMouseOver={e => clientSubTab !== 'general' && (e.target.style.background = '#e9ecef')}
                    onMouseOut={e => clientSubTab !== 'general' && (e.target.style.background = 'transparent')}
                  >
                    General & Widget Settings
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setClientSubTab('fullpage')}
                    style={{
                      flex: '1',
                      padding: '16px 24px',
                      border: 'none',
                      background: clientSubTab === 'fullpage' ? '#3a4f90' : 'transparent',
                      color: clientSubTab === 'fullpage' ? 'white' : '#666',
                      cursor: 'pointer',
                      fontSize: '1em',
                      fontWeight: clientSubTab === 'fullpage' ? '600' : '500',
                      borderBottom: clientSubTab === 'fullpage' ? '3px solid #3a4f90' : 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={e => clientSubTab !== 'fullpage' && (e.target.style.background = '#e9ecef')}
                    onMouseOut={e => clientSubTab !== 'fullpage' && (e.target.style.background = 'transparent')}
                  >
                    Full Page Setup
                  </button>
                </div>
                
                {/* General & Widget Settings Sub-Tab */}
                {clientSubTab === 'general' && (
                  <div style={{ display: 'flex', gap: 32 }}>
                    <div style={{ flex: '0 1 50%', minWidth: 320 }}>
                      <h3 style={{ fontSize: '1.1em', margin: '0 0 10px 0', color: '#3a4f90' }}>General & Widget Settings</h3>
                      
                      {/* General Settings Section */}
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ fontSize: '1em', margin: '0 0 8px 0', color: '#666', borderBottom: '1px solid #e0e0e0', paddingBottom: '4px' }}>General Settings</h4>
              <table style={{ marginBottom: 8, fontSize: "0.95em" }}>
                <tbody>
                                      <tr>
                      <td style={{ padding: 8, textAlign: 'right' }}><label>Client-Location ID:</label></td>
                              <td style={{ padding: 8 }}><input name="clientId" value={form.clientId} onChange={handleChange} style={{ width: 200 }} placeholder="GHL Location ID" /></td>
                    </tr>
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Company Name:</label></td>
                              <td style={{ padding: 8 }}><input name="companyName" value={form.companyName} onChange={handleChange} style={{ width: 200 }} /></td>
                  </tr>
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Email Address:</label></td>
                              <td style={{ padding: 8 }}><input name="email" type="email" value={form.email} onChange={handleChange} style={{ width: 200 }} placeholder="For password reset links" /></td>
                  </tr>
                  <tr hidden={isClientView}>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Account Type:</label></td>
                    <td style={{ padding: 8 }}>
                      <select 
                        name="accountType" 
                        value={form.accountType} 
                        onChange={(e) => {
                          const newAccountType = e.target.value;
                          setForm(prev => ({
                            ...prev,
                            accountType: newAccountType,
                            keyType: (newAccountType === 'universal_agent' || newAccountType === 'agency_api_specific_agent') ? 'shared' : 'client',
                            billingEnabled: newAccountType === 'universal_agent'
                          }));
                          // Reset the saved flag when switching account types
                          setUniversalAgentSaved(false);
                        }} 
                        disabled={!isAuthed}
                        style={{ 
                          width: 200,
                          opacity: isAuthed ? 1 : 0.6,
                          cursor: isAuthed ? 'pointer' : 'not-allowed'
                        }}
                        title={!isAuthed ? "Only administrators can change account type" : ""}
                      >
                        <option value="client_specific">Client-Specific Keys</option>
                        <option value="universal_agent">Universal Agent (Shared)</option>
                        <option value="agency_api_specific_agent">Agency API + Specific Agent</option>
                      </select>
                      {!isAuthed && (
                        <div style={{ fontSize: '0.8em', color: '#666', marginTop: '4px' }}>
                          Only administrators can change account type
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>CRM Integration:</label></td>
                    <td style={{ padding: 8 }}>
                      <select name="crmType" value={form.crmType} onChange={handleChange} style={{ width: 200 }}>
                        <option value="none">No CRM Integration</option>
                        <option value="ghl">GoHighLevel</option>
                        <option value="google" disabled>Google Calendar (Coming Soon)</option>
                      </select>
                    </td>
                  </tr>
                  {form.crmType === 'ghl' && (
                    <tr>
                      <td style={{ padding: 8, textAlign: 'right' }}><label>GHL Calendar ID:</label></td>
                              <td style={{ padding: 8 }}><input name="calendarId" value={form.calendarId} onChange={handleChange} style={{ width: 200 }} placeholder="GHL Calendar ID" /></td>
                    </tr>
                  )}
                  
                  {/* Billing Settings for Universal Agents */}
                  {form.accountType === 'universal_agent' && (
                    <>
                      <tr>
                        <td style={{ padding: 8, textAlign: 'right' }}><label style={{ color: '#1976d2', fontWeight: 'bold' }}>Billing Settings:</label></td>
                        <td style={{ padding: 8 }}>
                          <div style={{ border: '2px solid #1976d2', padding: 10, borderRadius: 4, backgroundColor: '#e3f2fd' }}>
                            <div style={{ marginBottom: 8 }}>
                              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input 
                                  type="checkbox" 
                                  name="billingEnabled" 
                                  checked={form.billingEnabled}
                                  onChange={(e) => setForm(prev => ({ ...prev, billingEnabled: e.target.checked }))}
                                  style={{ marginRight: 6 }}
                                />
                                <span>Enable Billing for Universal Agent</span>
                              </label>
                            </div>
                            {form.billingEnabled && (
                              <div style={{ marginTop: 8 }}>
                                <label style={{ display: 'block', marginBottom: 4 }}>Billing Tier:</label>
                                <select name="billingTier" value={form.billingTier} onChange={handleChange} style={{ width: 200 }}>
                                  <option value="500min">500 minutes</option>
                                  <option value="1000min">1000 minutes</option>
                                  <option value="unlimited">Unlimited</option>
                                </select>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    </>
                  )}
                  {(!isClientMode || !form.hideSensitiveFields) && (
                    <>
                      {/* Agent ID field removed - now only shown in API Key Status section */}
                    </>
                  )}
                  
                  {/* API Key Status (Admin Only) */}
                  {isAuthed && (
                    <>
                      <tr>
                        <td style={{ padding: 8, textAlign: 'right' }}><label style={{ color: '#1976d2', fontWeight: 'bold' }}>API Key Status:</label></td>
                        <td style={{ padding: 8 }}>
                          <div style={{ border: '2px solid #1976d2', padding: 10, borderRadius: 4, backgroundColor: '#e3f2fd' }}>
                            
                            {/* Show shared API setup for shared-key modes */}
                            {(form.accountType === 'universal_agent' || form.accountType === 'agency_api_specific_agent') && (
                              <div style={{ marginBottom: 12 }}>
                                <p style={{ margin: '0 0 8px 0', fontSize: '0.9em', color: '#666' }}>
                                  <strong>Current Setup:</strong>
                                  <button 
                                    onClick={checkEnvStatus}
                                    style={{ 
                                      marginLeft: 8, 
                                      padding: '2px 8px', 
                                      fontSize: '0.8em', 
                                      background: '#1976d2', 
                                      color: 'white', 
                                      border: 'none', 
                                      borderRadius: 3, 
                                      cursor: 'pointer' 
                                    }}
                                  >
                                    Refresh
                                  </button>
                                </p>
                                <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '12px' }}>
                                  <div>{form.accountType === 'universal_agent' ? 'Universal Agent (Shared Keys) Available' : 'Agency Shared API Key Mode'}</div>
                                  <div>{envStatus.retellApiKey ? 'Yes' : 'No'} Retell API Key: {envStatus.retellApiKey ? 'Configured' : 'Not Set'}</div>
                                  <div>{envStatus.agentId ? 'Yes' : 'No'} Shared Agent ID: {envStatus.agentId ? 'Configured' : 'Not Set'}</div>
                                </div>
                              </div>
                            )}

                            {/* Client-Specific Keys Input */}
                            {form.accountType === 'client_specific' && (
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ marginBottom: 8 }}>
                                  <label style={{ display: 'block', marginBottom: 4 }}>Retell API Key:</label>
                                  <input 
                                    type="password" 
                                    id="tempRetellKey"
                                    placeholder="Enter client's Retell API key"
                                    style={{ width: 300, padding: 4 }}
                                    onChange={(e) => {
                                      // Don't auto-clear for better UX
                                      // setTimeout(() => {
                                      //   if (e.target) e.target.value = '';
                                      // }, 5000);
                                    }}
                                  />
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                  <label style={{ display: 'block', marginBottom: 4 }}>Agent ID:</label>
                                  <input 
                                    type="text" 
                                    id="tempAgentId"
                                    placeholder="Enter client's Agent ID"
                                    style={{ width: 300, padding: 4 }}
                                    onChange={(e) => {
                                      // Don't auto-clear for better UX
                                      // setTimeout(() => {
                                      //   if (e.target) e.target.value = '';
                                      // }, 5000);
                                    }}
                                  />
                                </div>
                                {/* GHL API Key - Always visible, always client-specific */}
                                <div style={{ marginBottom: 8 }}>
                                  <label style={{ display: 'block', marginBottom: 4 }}>
                                    GHL API Key (Optional): 
                                    {form.crmType === 'ghl' && (
                                      <span style={{ color: '#1976d2', fontSize: '0.8em', marginLeft: '8px' }}>
                                        CRM Integration Enabled
                                      </span>
                                    )}
                                  </label>
                                  <input 
                                    type="password" 
                                    id="tempGhlKey"
                                    placeholder="Enter client's GHL API key (optional)"
                                    style={{ width: 300, padding: 4 }}
                                    onChange={(e) => {
                                      // Don't auto-clear for better UX
                                      // setTimeout(() => {
                                      //   if (e.target) e.target.value = '';
                                      // }, 5000);
                                    }}
                                  />
                                </div>
                                <button 
                                  onClick={() => {
                                    const retellKey = document.getElementById('tempRetellKey').value;
                                    const agentId = document.getElementById('tempAgentId').value;
                                    const ghlKey = document.getElementById('tempGhlKey').value;
                                    
                                    if (retellKey && agentId) {
                                      // Add to form state temporarily for save
                                      setForm(prev => ({
                                        ...prev,
                                        tempRetellKey: retellKey,
                                        tempAgentId: agentId,
                                        agentId: agentId, // Also set the main agentId field
                                        tempGhlKey: ghlKey
                                      }));
                                      
                                      // Don't clear input fields - let user see their values
                                      // document.getElementById('tempRetellKey').value = '';
                                      // document.getElementById('tempAgentId').value = '';
                                      // document.getElementById('tempGhlKey').value = '';
                                      
                                      alert('Client-specific keys captured securely. Click Save to store them.');
                                    } else {
                                                                              alert('Please enter both Retell API key and Agent ID.');
                                    }
                                  }}
                                  style={{
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    border: 'none',
                                    padding: '6px 12px',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontSize: '0.9em'
                                  }}
                                >
                                  Capture Client Keys Securely
                                </button>
                              </div>
                            )}

                            {/* Agency API + Specific Agent Input */}
                            {form.accountType === 'agency_api_specific_agent' && (
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ marginBottom: 8 }}>
                                  <label style={{ display: 'block', marginBottom: 4 }}>Agent ID:</label>
                                  <input
                                    type="text"
                                    id="tempAgentId"
                                    placeholder="Enter client's Agent ID"
                                    style={{ width: 300, padding: 4 }}
                                  />
                                </div>
                                {/* GHL API Key - Always client-specific */}
                                <div style={{ marginBottom: 8 }}>
                                  <label style={{ display: 'block', marginBottom: 4 }}>
                                    GHL API Key (Optional):
                                    {form.crmType === 'ghl' && (
                                      <span style={{ color: '#1976d2', fontSize: '0.8em', marginLeft: '8px' }}>
                                        CRM Integration Enabled
                                      </span>
                                    )}
                                  </label>
                                  <input
                                    type="password"
                                    id="tempGhlKey"
                                    placeholder="Enter client's GHL API key (optional)"
                                    style={{ width: 300, padding: 4 }}
                                  />
                                </div>
                                <button
                                  onClick={() => {
                                    const agentId = document.getElementById('tempAgentId').value;
                                    const ghlKey = document.getElementById('tempGhlKey').value;

                                    if (agentId) {
                                      setForm(prev => ({
                                        ...prev,
                                        tempAgentId: agentId,
                                        agentId: agentId,
                                        tempGhlKey: ghlKey
                                      }));

                                      alert('Agent ID captured securely. Click Save to store it.');
                                    } else {
                                      alert('Please enter an Agent ID.');
                                    }
                                  }}
                                  style={{
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    border: 'none',
                                    padding: '6px 12px',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontSize: '0.9em'
                                  }}
                                >
                                  Capture Agent ID Securely
                                </button>
                              </div>
                            )}

                            {/* Current Client Configuration Status */}
                            <div style={{ marginTop: 12, padding: 8, backgroundColor: '#f9f9f9', borderRadius: 4 }}>
                              <p style={{ margin: '0 0 8px 0', fontSize: '0.9em', fontWeight: 'bold' }}>Current Client Configuration:</p>
                              <div style={{ fontSize: '0.85em', color: '#666' }}>
                                {/* Retell API Key Status */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                  <span>Retell API Key: {form.keyType === 'shared' ? (universalAgentSaved ? 'Using Agency Shared API Key' : 'Pending Save') : (form.tempRetellKey || form.hasRetellApiKey) ? 'Configured' : 'Not Set'}</span>
                                  {form.keyType === 'client' && (form.tempRetellKey || form.hasRetellApiKey) && (
                                    <button
                                      onClick={() => {
                                        document.getElementById('tempRetellKey').value = '';
                                        setForm(prev => ({ ...prev, tempRetellKey: '', hasRetellApiKey: false }));
                                      }}
                                      style={{
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        padding: '2px 6px',
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        fontSize: '0.7em',
                                        marginLeft: '8px'
                                      }}
                                      title="Clear Retell API Key"
                                    >
                                      x
                                    </button>
                                  )}
                                </div>
                                
                                {/* Agent ID Status */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                  <span>Agent ID: {form.accountType === 'universal_agent' ? (universalAgentSaved ? 'Using Shared Agent ID' : 'Pending Save') : form.agentId ? 'Configured' : 'Not Set'}</span>
                                  {form.accountType !== 'universal_agent' && form.agentId && (
                                    <button
                                      onClick={() => {
                                        setForm(prev => ({ ...prev, agentId: '' }));
                                      }}
                                      style={{
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        padding: '2px 6px',
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        fontSize: '0.7em',
                                        marginLeft: '8px'
                                      }}
                                      title="Clear Agent ID"
                                    >
                                      x
                                    </button>
                                  )}
                                </div>
                                
                                                                {/* GHL API Key Status - Only show when CRM is set to GHL */}
                                {form.crmType === 'ghl' && (
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <span>GHL API Key: {(form.tempGhlKey || form.hasGhlApiKey) ? 'Configured' : 'Not Set'}</span>
                                    {(form.tempGhlKey || form.hasGhlApiKey) && (
                                      <button
                                        onClick={() => {
                                          document.getElementById('tempGhlKey').value = '';
                                          setForm(prev => ({ ...prev, tempGhlKey: '', hasGhlApiKey: false }));
                                        }}
                                        style={{
                                          backgroundColor: '#dc3545',
                                          color: 'white',
                                          border: 'none',
                                          padding: '2px 6px',
                                          borderRadius: 2,
                                          cursor: 'pointer',
                                          fontSize: '0.7em',
                                          marginLeft: '8px'
                                        }}
                                        title="Clear GHL API Key"
                                      >
                                        Ã—
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                          </div>
                        </td>
                      </tr>
                    </>
                  )}
                  <tr hidden={isClientView}>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Hide API Keys, Agent ID & Footer from Client:</label></td>
                    <td style={{ padding: 8 }}><input type="checkbox" name="hideSensitiveFields" checked={form.hideSensitiveFields} onChange={handleChange} /></td>
                  </tr>
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Primary Color:</label></td>
                              <td style={{ padding: 8 }}><input type="color" name="primary" value={expandHex(form.primary)} onChange={handleChange} style={{ marginRight: 8 }} /><input name="primary" value={form.primary} onChange={handleChange} pattern="#?[0-9A-Fa-f]{6}" maxLength={7} style={{ width: 90 }} /></td>
                  </tr>
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Secondary Color:</label></td>
                              <td style={{ padding: 8 }}><input type="color" name="secondary" value={expandHex(form.secondary)} onChange={handleChange} style={{ marginRight: 8 }} /><input name="secondary" value={form.secondary} onChange={handleChange} pattern="#?[0-9A-Fa-f]{6}" maxLength={7} style={{ width: 90 }} /></td>
                  </tr>
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Background Color:</label></td>
                              <td style={{ padding: 8 }}><input type="color" name="background" value={expandHex(form.background)} onChange={handleChange} style={{ marginRight: 8 }} /><input name="background" value={form.background} onChange={handleChange} pattern="#?[0-9A-Fa-f]{6}" maxLength={7} style={{ width: 90 }} /></td>
                  </tr>
                  <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Transparent Widget Background:</label></td>
                              <td style={{ padding: 8 }}><input type="checkbox" name="transparentWidgetBg" checked={form.transparentWidgetBg} onChange={handleChange} /></td>
                  </tr>
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Font Family:</label></td>
                    <td style={{ padding: 8 }}><input name="fontFamily" value={form.fontFamily} onChange={handleChange} style={{ width: 180 }} /></td>
                  </tr>
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Font Size:</label></td>
                    <td style={{ padding: 8 }}><input name="fontSize" type="number" min={10} max={32} step={1} value={form.fontSize} onChange={handleChange} style={{ width: 60 }} /> px</td>
                  </tr>
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Icon Type:</label></td>
                    <td style={{ padding: 8 }}>
                      <select name="iconType" value={form.iconType} onChange={handleChange} style={{ width: 120, marginRight: 8 }}>
                        {availableIcons.map(icon => (
                          <option key={icon.id} value={icon.id}>{icon.name}</option>
                        ))}
                      </select>
                      <input
                        type="color"
                        name="iconColor"
                        value={expandHex(form.iconColor)}
                        onChange={handleChange}
                        style={{ marginRight: 8 }}
                      />
                      <input
                        name="iconColor"
                        value={form.iconColor}
                        onChange={handleChange}
                        pattern="#?[0-9A-Fa-f]{6}"
                        maxLength={7}
                        style={{ width: 90 }}
                      />
                      <div style={{ display: 'inline-block', marginLeft: 8, verticalAlign: 'middle' }}>
                        {form.iconType === 'mic' && <MicIcon color={expandHex(form.iconColor)} size={20} />}
                        {form.iconType === 'headset' && <HeadsetIcon color={expandHex(form.iconColor)} size={20} />}
                        {form.iconType === 'user' && <UserIcon color={expandHex(form.iconColor)} size={20} />}
                        {form.iconType === 'classic-mic' && <ClassicMicIcon color={expandHex(form.iconColor)} size={20} />}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Avatar:</label></td>
                    <td style={{ padding: 8 }}>
                      <select name="avatarType" value={form.avatarType} onChange={handleChange} style={{ width: 150 }}>
                        {avatarFiles.map(f => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                      <div style={{ display: 'inline-block', marginLeft: 8, verticalAlign: 'middle' }}>
                        <img 
                          src={`/${form.avatarType}`} 
                          alt="Avatar Preview" 
                          style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }}
                        />
                      </div>
                      <label style={{ marginLeft: 12, fontSize: '0.9em' }}>
                        <input type="file" accept=".png,.jpg,.jpeg" style={{ display: 'none' }} onChange={handleAvatarUpload} disabled={uploadingAvatar} />
                        <span style={{ cursor: 'pointer', color: '#3a4f90', border: '1px solid #3a4f90', borderRadius: 4, padding: '2px 8px', background: uploadingAvatar ? '#eee' : '#fff' }}>{uploadingAvatar ? 'Uploading...' : 'Upload Avatar'}</span>
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Show Agent/User Role Icons:</label></td>
                    <td style={{ padding: 8 }}><input type="checkbox" name="showRoleIcons" checked={form.showRoleIcons} onChange={handleChange} /></td>
                  </tr>
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Show Glow:</label></td>
                    <td style={{ padding: 8 }}><input type="checkbox" name="showGlow" checked={form.showGlow} onChange={handleChange} /></td>
                  </tr>
                  {form.showGlow && (
                    <>
                      <tr>
                        <td style={{ padding: 8, textAlign: 'right' }}><label>Glow Size Multiplier:</label></td>
                        <td style={{ padding: 8 }}><input name="glowSize" type="number" min={0.5} max={3.0} step={0.1} value={form.glowSize || 1.2} onChange={handleChange} style={{ width: 60 }} /> x mic size</td>
                      </tr>
                      <tr>
                        <td style={{ padding: 8, textAlign: 'right' }}><label>Glow Blur:</label></td>
                        <td style={{ padding: 8 }}><input name="glowBlur" type="number" min={1} max={50} value={form.glowBlur || 22} onChange={handleChange} style={{ width: 60 }} /> px</td>
                      </tr>
                      <tr>
                        <td style={{ padding: 8, textAlign: 'right' }}><label>Glow Brightness:</label></td>
                        <td style={{ padding: 8 }}><input name="glowBrightness" type="number" min={0.1} max={3.0} step={0.1} value={form.glowBrightness || 1.2} onChange={handleChange} style={{ width: 60 }} /></td>
                      </tr>
                      <tr>
                        <td style={{ padding: 8, textAlign: 'right' }}><label>Glow Opacity:</label></td>
                        <td style={{ padding: 8 }}><input name="glowOpacity" type="number" min={0.1} max={1.0} step={0.1} value={form.glowOpacity || 0.8} onChange={handleChange} style={{ width: 60 }} /></td>
                      </tr>
                      <tr>
                        <td style={{ padding: 8, textAlign: 'right' }}><label>Glow Animation Speed:</label></td>
                        <td style={{ padding: 8 }}><input name="glowSpeed" type="number" min={0.5} max={10.0} step={0.5} value={form.glowSpeed || 7.0} onChange={handleChange} style={{ width: 60 }} /> seconds</td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Show Waves:</label></td>
                    <td style={{ padding: 8 }}><input type="checkbox" name="showWaves" checked={form.showWaves} onChange={handleChange} /></td>
                  </tr>

                          </tbody>
                        </table>
                      </div>
                      
                      {/* Widget-Specific Settings Section */}
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ fontSize: '1em', margin: '0 0 8px 0', color: '#666', borderBottom: '1px solid #e0e0e0', paddingBottom: '4px' }}>Widget-Specific Settings</h4>
                        <table style={{ marginBottom: 8, fontSize: "0.95em" }}>
                          <tbody>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Transcript Background:</label></td>
                              <td style={{ padding: 8 }}><input type="color" name="cardBg" value={expandHex(form.cardBg)} onChange={handleChange} style={{ marginRight: 8 }} /><input name="cardBg" value={form.cardBg} onChange={handleChange} pattern="#?[0-9A-Fa-f]{6}" maxLength={7} style={{ width: 90 }} /></td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Transcript Text Color:</label></td>
                              <td style={{ padding: 8 }}><input type="color" name="cardText" value={expandHex(form.cardText)} onChange={handleChange} style={{ marginRight: 8 }} /><input name="cardText" value={form.cardText} onChange={handleChange} pattern="#?[0-9A-Fa-f]{6}" maxLength={7} style={{ width: 90 }} /></td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Buttons Background:</label></td>
                              <td style={{ padding: 8 }}><input type="color" name="buttonBg" value={expandHex(form.buttonBg || form.primary)} onChange={handleChange} style={{ marginRight: 8 }} /><input name="buttonBg" value={form.buttonBg || form.primary} onChange={handleChange} pattern="#?[0-9A-Fa-f]{6}" maxLength={7} style={{ width: 90 }} /></td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Button Text Color:</label></td>
                              <td style={{ padding: 8 }}><input type="color" name="buttonText" value={expandHex(form.buttonText)} onChange={handleChange} style={{ marginRight: 8 }} /><input name="buttonText" value={form.buttonText} onChange={handleChange} pattern="#?[0-9A-Fa-f]{6}" maxLength={7} style={{ width: 90 }} /></td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Button Text Content:</label></td>
                              <td style={{ padding: 8 }}><input name="buttonTextContent" value={form.buttonTextContent} onChange={handleChange} style={{ width: 200 }} placeholder="e.g., Talk to AI Assistant" /></td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Button Text Size:</label></td>
                              <td style={{ padding: 8 }}><input name="buttonTextSize" type="number" min={10} max={24} value={form.buttonTextSize || 14} onChange={handleChange} style={{ width: 60 }} /> px</td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Button Hover:</label></td>
                              <td style={{ padding: 8 }}><input type="color" name="buttonHover" value={expandHex(form.buttonHover)} onChange={handleChange} style={{ marginRight: 8 }} /><input name="buttonHover" value={form.buttonHover} onChange={handleChange} pattern="#?[0-9A-Fa-f]{6}" maxLength={7} style={{ width: 90 }} /></td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Open Transcript On Call Start (Widget):</label></td>
                              <td style={{ padding: 8 }}>
                                <input type="checkbox" name="transcriptDefaultOpenWidget" checked={form.transcriptDefaultOpenWidget} onChange={handleChange} />
                                <span style={{ marginLeft: 8, fontSize: '0.85em', color: '#666' }}>When enabled, transcript opens as soon as the mic call starts.</span>
                              </td>
                            </tr>
                  <tr>
                    <td style={{ padding: 8, textAlign: 'right' }}><label>Mic Button Sizing:</label></td>
                    <td style={{ padding: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label style={{ fontSize: '0.9em' }}>
                          <input 
                            type="radio" 
                            name="layout.widget.mode" 
                            value="fixed" 
                            checked={form.layout?.widget?.mode === 'fixed'} 
                            onChange={handleChange}
                            style={{ marginRight: '4px' }}
                          />
                          Fixed
                        </label>
                        <label style={{ fontSize: '0.9em' }}>
                          <input 
                            type="radio" 
                            name="layout.widget.mode" 
                            value="responsive" 
                            checked={form.layout?.widget?.mode === 'responsive'} 
                            onChange={handleChange}
                            style={{ marginRight: '4px' }}
                          />
                          Responsive
                        </label>
                      </div>
                    </td>
                  </tr>
                  {form.layout?.widget?.mode === 'fixed' && (
                    <tr>
                      <td style={{ padding: 8, textAlign: 'right' }}><label>Mic Button Size:</label></td>
                      <td style={{ padding: 8 }}><input name="layout.widget.size" type="number" min={40} max={500} value={form.layout?.widget?.size || 160} onChange={handleChange} style={{ width: 60 }} /> px</td>
                    </tr>
                  )}
                  {form.layout?.widget?.mode === 'responsive' && (
                    <>
                      <tr>
                        <td style={{ padding: 8, textAlign: 'right' }}><label>Min Size:</label></td>
                        <td style={{ padding: 8 }}><input name="layout.widget.min" type="number" min={40} max={500} value={form.layout?.widget?.min || 120} onChange={handleChange} style={{ width: 60 }} /> px</td>
                      </tr>
                      <tr>
                        <td style={{ padding: 8, textAlign: 'right' }}><label>Max Size:</label></td>
                        <td style={{ padding: 8 }}><input name="layout.widget.max" type="number" min={40} max={500} value={form.layout?.widget?.max || 190} onChange={handleChange} style={{ width: 60 }} /> px</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
              </div>
                    
                    {/* Preview Panel */}
                    <div style={{
                      ...previewPanelStyle,
                      position: stickyPreview ? 'sticky' : 'relative',
                      top: stickyPreview ? '20px' : 'auto',
                      zIndex: stickyPreview ? 1000 : 'auto',
                      transition: 'all 0.3s ease',
                      maxHeight: stickyPreview ? 'calc(100vh - 40px)' : previewPanelStyle.maxHeight,
                      overflowY: stickyPreview ? 'auto' : 'visible'
                    }}>
                      <h3 style={{ fontSize: '1.1em', margin: '0 0 10px 0', textAlign: 'center', color: '#3a4f90' }}>Widget Preview</h3>
                      <button onClick={(e) => { e.preventDefault(); setShowAvatarPreview(v => !v); }} style={{ marginBottom: 10, background: '#e0e0e0', color: '#3a4f90', border: '1px solid #3a4f90', borderRadius: 6, padding: '4px 14px', fontWeight: 500, cursor: 'pointer', fontSize: '0.98em' }}>
                        {showAvatarPreview ? 'Show Mic Icon' : 'Show Avatar'}
                      </button>
                      {/* Mic circle preview with glow and static waves */}
                      <div style={{ 
                        position: 'relative', 
                        width: glowSize, 
                        height: glowSize, 
                        marginBottom: 18, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center'
                      }}>
                {form.showGlow && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: micSize * (form.glowSize || 1.2),
                      height: micSize * (form.glowSize || 1.2),
                      transform: 'translate(-50%, -50%)',
                      borderRadius: '50%',
                      background: `conic-gradient(from 0deg, ${expandHex(form.primary)}, ${expandHex(form.secondary)}, ${expandHex(form.primary)})`,
                      zIndex: 0,
                      filter: `blur(${form.glowBlur || 22}px) brightness(${form.glowBrightness || 1.2})`,
                      opacity: form.glowOpacity || 0.8,
                      animation: `adminRotateGlow ${form.glowSpeed || 3.5}s linear infinite`,
                      pointerEvents: 'none'
                    }}
                  ></div>
                )}
                {/* Mic circle */}
                <div style={{ width: micSize, height: micSize, borderRadius: '50%', background: micCircleBg, border: '1px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2, overflow: 'hidden' }}>
                  {showAvatarPreview ? (
                    <img src={`/${form.avatarType}`} alt="Avatar" style={{ width: micSize * 0.875, height: micSize * 0.875, borderRadius: '50%', objectFit: 'cover', border: 'none' }} />
                  ) : (
                    <>
                      {form.iconType === 'mic' && <MicIcon color={expandHex(form.iconColor)} size={micSize * 0.5} />}
                      {form.iconType === 'classic-mic' && <ClassicMicIcon color={expandHex(form.iconColor)} size={micSize * 0.5} />}
                      {form.iconType === 'headset' && <HeadsetIcon color={expandHex(form.iconColor)} size={micSize * 0.5} />}
                      {form.iconType === 'user' && <UserIcon color={expandHex(form.iconColor)} size={micSize * 0.5} />}
                    </>
                  )}
                </div>
                {form.showWaves && showAvatarPreview && [1, 2, 3].map(i => (
                  <div key={i} style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: micSize + i * 18,
                    height: micSize + i * 18,
                    borderRadius: '50%',
                    border: `2px solid ${expandHex(form.secondary)}AA`,
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1,
                    pointerEvents: 'none',
                  }} />
                ))}
              </div>
              {/* Button preview */}
              <button style={{
                background: expandHex(form.buttonBg || form.primary),
                color: expandHex(form.buttonText),
                border: 'none',
                borderRadius: 8,
                padding: '12px 32px',
                fontWeight: 600,
                fontSize: `${form.buttonTextSize || 14}px`,
                marginBottom: 18,
                boxShadow: '0 2px 8px #0001',
                cursor: 'pointer',
                transition: 'background 0.2s',
                width: 180,
                marginTop: 6
              }}
                onMouseOver={e => e.currentTarget.style.background = expandHex(form.buttonHover || form.buttonBg || form.primary)}
                onMouseOut={e => e.currentTarget.style.background = expandHex(form.buttonBg || form.primary)}
              >Sample Button</button>
              {/* Transcript preview below button */}
              <div style={{ background: expandHex(form.cardBg), color: expandHex(form.cardText), borderRadius: 12, padding: '14px 14px', marginTop: 22, minHeight: 40, width: 260, boxShadow: '0 2px 8px #0001', fontFamily: form.fontFamily, fontSize: Number(form.fontSize || 14) || 14 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Transcript</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ borderLeft: `4px solid ${expandHex(form.secondary)}`, paddingLeft: 10, background: 'rgba(255,255,255,0.7)', borderRadius: 6 }}>
                    {form.showRoleIcons && <span title="User" aria-label="User" style={{ marginRight: 4 }}>[User]</span>}User: Hello, this is a sample transcript message.
                  </div>
                  <div style={{ borderLeft: `4px solid ${expandHex(form.secondary)}`, paddingLeft: 10, background: 'rgba(255,255,255,0.7)', borderRadius: 6 }}>
                    {form.showRoleIcons && <span title="Agent" aria-label="Agent" style={{ marginRight: 4 }}>[Agent]</span>}Agent: Hi! How can I help you today?
                  </div>
                </div>
              </div>
            </div>
          </div>
                )}
                
                {/* General & Widget Settings Save Buttons */}
                {clientSubTab === 'general' && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: '16px' }}>
                    <button 
                      type="submit"
                      style={{ 
                        padding: "10px 20px", 
                        fontSize: "0.95em", 
                        background: "#3a4f90", 
                        color: "white", 
                        border: "none", 
                        borderRadius: "4px", 
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                      onMouseOver={e => e.target.style.background = "#2c3e50"}
                      onMouseOut={e => e.target.style.background = "#3a4f90"}
                      onMouseDown={e => e.target.style.transform = "scale(0.98)"}
                      onMouseUp={e => e.target.style.transform = "scale(1)"}
                    >
                      Save Changes
                    </button>
                    
                    {editId && (
                      <button 
                        type="button" 
                        onClick={() => {
                          setEditId(null);
                          setForm({
                            clientId: "",
                            companyName: "",
                            agentId: "",
                            primary: "#bbbbbb",
                            secondary: "#000000",
                            background: "#aaaaaa",
                            transparentWidgetBg: true,
                            bgGradient: false,
                            buttonGradient: false,
                            cardBg: "#e0e0e0",
                            cardText: "#454545",
                            buttonBg: "",
                            buttonText: "#ffffff",
                            buttonHover: "",
                            transcriptDefaultOpenFull: true,
                            transcriptDefaultOpenWidget: false,
                            transparentBackgroundFull: false,
                            transparentBackgroundWidget: true,
                            fontFamily: "Montserrat, Arial, sans-serif",
                            fontSize: "14",
                            iconType: "classic-mic",
                            iconColor: "#ffffff",
                            avatarType: "avatar.png",
                            showRoleIcons: false,
                            minSize: 120,
                            defaultSize: 160,
                            maxSize: 190,
                            layout: {
                              widget: { mode: 'fixed', size: 160, min: 120, max: 190 },
                              full: { mode: 'fixed', size: 160, min: 120, max: 190 },
                            },
                            showGlow: true,
                            showWaves: true,
                            fullPageWidth: 800,
                            fullPageHeight: 'auto',
                            fullPageHeadline: "Get your (Offer Here)!",
                            fullPageSubheadline: "Click to talk to our AI Assistant",
                            fullPageFooter: "Â© 2025 Powered by Sellflows.com -- All rights reserved.",
                            headlineFontSize: 28,
                            subheadlineFontSize: 18,
                            headlineColor: "primary",
                            headlineCustomColor: "#6ec6ff",
                            subheadlineColor: "secondary",
                            subheadlineCustomColor: "#ff6ec4",
                            footerColor: "secondary",
                            footerCustomColor: "#ffffff",
                            backgroundFull: "",
                            buttonBgFull: "",
                            buttonTextFull: "#ffffff",
                            cardBgFull: "",
                            cardTextFull: "",
                            password: "",
                            hideSensitiveFields: true,
                            autoSendPasswordLink: false,
                            backgroundFullType: "primary",
                            buttonBgFullType: "primary",
                            buttonTextFullType: "primary",
                            cardBgFullType: "primary",
                            cardTextFullType: "primary",
                            backgroundFullCustom: "",
                            buttonBgFullCustom: "",
                            buttonTextFullCustom: "",
                            cardBgFullCustom: "",
                            cardTextFullCustom: "",
                            transcriptFontSize: 14,
                          });
                          setEditId(null);
                          setError("");
                          setSuccess("");
                          setClientSubTab('general');
                        }}
                        style={{ 
                          padding: "10px 20px", 
                          fontSize: "0.95em", 
                          background: "#6c757d", 
                          color: "white", 
                          border: "none", 
                          borderRadius: "4px", 
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                        onMouseOver={e => e.target.style.background = "#5a6268"}
                        onMouseOut={e => e.target.style.background = "#6c757d"}
                        onMouseDown={e => e.target.style.transform = "scale(0.98)"}
                        onMouseUp={e => e.target.style.transform = "scale(1)"}
                      >
                        Done
                      </button>
                    )}
                    
                    {/* Widget Preview Link */}
                    {editId && (
                      <button
                        type="button"
                        onClick={() => window.open(`/?client=${editId}&mode=widget`, '_blank')}
                        style={{ 
                          padding: "10px 20px", 
                          fontSize: "0.95em", 
                          background: "linear-gradient(135deg, #3a4f90, #19647e)", 
                          color: "white", 
                          border: "1px solid #2c3e50", 
                          borderRadius: "4px", 
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          textDecoration: "none",
                          fontWeight: "600",
                          textShadow: "0 1px 2px rgba(0,0,0,0.3)"
                        }}
                        onMouseOver={e => e.target.style.background = "linear-gradient(135deg, #2c3e50, #19647e)"}
                        onMouseOut={e => e.target.style.background = "linear-gradient(135deg, #3a4f90, #19647e)"}
                        onMouseDown={e => e.target.style.transform = "scale(0.98)"}
                        onMouseUp={e => e.target.style.transform = "scale(1)"}
                      >
                        Widget Preview
                      </button>
                    )}
                    
                    {/* Iframe Export Link */}
                    {editId && (
                      <button
                        type="button"
                        onClick={() => window.open(`/iframe-demo?client=${editId}&returnTo=${isClientMode ? 'client' : 'admin'}`, '_blank')}
                        style={{ 
                          padding: "10px 20px", 
                          fontSize: "0.95em", 
                          background: "linear-gradient(135deg, #28a745, #20c997)", 
                          color: "white", 
                          border: "1px solid #1e7e34", 
                          borderRadius: "4px", 
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          textDecoration: "none",
                          fontWeight: "600",
                          textShadow: "0 1px 2px rgba(0,0,0,0.3)"
                        }}
                        onMouseOver={e => e.target.style.background = "linear-gradient(135deg, #1e7e34, #20c997)"}
                        onMouseOut={e => e.target.style.background = "linear-gradient(135deg, #28a745, #20c997)"}
                        onMouseDown={e => e.target.style.transform = "scale(0.98)"}
                        onMouseUp={e => e.target.style.transform = "scale(1)"}
                      >
                        Iframe Export
                      </button>
                    )}
                  </div>
                )}
                
                {/* Full Page Setup Sub-Tab */}
                {clientSubTab === 'fullpage' && (
                  <div style={{ padding: '20px' }}>
                    <h3 style={{ marginBottom: '20px', color: '#3a4f90' }}>Full Page Setup</h3>
                    <p style={{ marginBottom: '20px', color: '#666', fontSize: '0.9em' }}>
                      Configure the full page version of your voice widget. These settings only affect the full page display, not the widget.
                    </p>
                    
                    <div style={{ display: 'flex', gap: 32 }}>
                      <div style={{ flex: '0 1 50%', minWidth: 320 }}>
                        <table style={{ marginBottom: 8, fontSize: "0.95em" }}>
                          <tbody>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Full Page Headline:</label></td>
                              <td style={{ padding: 8 }}><input name="fullPageHeadline" value={form.fullPageHeadline} onChange={handleChange} style={{ width: 300 }} /></td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Headline Font Size:</label></td>
                              <td style={{ padding: 8 }}><input name="headlineFontSize" type="number" min={12} max={72} value={form.headlineFontSize || 28} onChange={handleChange} style={{ width: 60 }} /> px</td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Headline Color:</label></td>
                              <td style={{ padding: 8 }}>
                                <select name="headlineColor" value={form.headlineColor} onChange={handleChange} style={{ width: 100, marginRight: 8 }}>
                                  <option value="primary">Primary</option>
                                  <option value="secondary">Secondary</option>
                                  <option value="custom">Custom</option>
                                </select>
                                {form.headlineColor === 'custom' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input 
                                      type="color" 
                                      name="headlineCustomColor" 
                                      value={expandHex(form.headlineCustomColor)} 
                                      onChange={handleChange} 
                                      style={{ width: 40, height: 30 }}
                                    />
                                    <input 
                                      type="text" 
                                      name="headlineCustomColor" 
                                      value={form.headlineCustomColor} 
                                      onChange={handleChange}
                                      placeholder="#ffffff"
                                      style={{ width: 80, height: 30, padding: '4px 8px', fontSize: '12px' }}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Headline Background:</label></td>
                              <td style={{ padding: 8 }}>
                                <select name="headlineBgType" value={form.headlineBgType} onChange={handleChange} style={{ width: 100, marginRight: 8 }}>
                                  <option value="primary">Primary</option>
                                  <option value="secondary">Secondary</option>
                                  <option value="custom">Custom</option>
                                </select>
                                {form.headlineBgType === 'custom' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input 
                                      type="color" 
                                      name="headlineBgCustom" 
                                      value={expandHex(form.headlineBgCustom || '#ffffff')} 
                                      onChange={handleChange} 
                                      style={{ width: 40, height: 30 }}
                                    />
                                    <input 
                                      type="text" 
                                      name="headlineBgCustom" 
                                      value={form.headlineBgCustom || '#ffffff'} 
                                      onChange={handleChange}
                                      placeholder="#ffffff"
                                      style={{ width: 80, height: 30, padding: '4px 8px', fontSize: '12px' }}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Full Page Subheadline:</label></td>
                              <td style={{ padding: 8 }}><input name="fullPageSubheadline" value={form.fullPageSubheadline} onChange={handleChange} style={{ width: 300 }} /></td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Subheadline Font Size:</label></td>
                              <td style={{ padding: 8 }}><input name="subheadlineFontSize" type="number" min={10} max={48} value={form.subheadlineFontSize || 18} onChange={handleChange} style={{ width: 60 }} /> px</td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Subheadline Color:</label></td>
                              <td style={{ padding: 8 }}>
                                <select name="subheadlineColor" value={form.subheadlineColor} onChange={handleChange} style={{ width: 100, marginRight: 8 }}>
                                  <option value="primary">Primary</option>
                                  <option value="secondary">Secondary</option>
                                  <option value="custom">Custom</option>
                                </select>
                                {form.subheadlineColor === 'custom' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input 
                                      type="color" 
                                      name="subheadlineCustomColor" 
                                      value={expandHex(form.subheadlineCustomColor)} 
                                      onChange={handleChange} 
                                      style={{ width: 40, height: 30 }}
                                    />
                                    <input 
                                      type="text" 
                                      name="subheadlineCustomColor" 
                                      value={form.subheadlineCustomColor} 
                                      onChange={handleChange}
                                      placeholder="#ffffff"
                                      style={{ width: 80, height: 30, padding: '4px 8px', fontSize: '12px' }}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Full Page Width:</label></td>
                              <td style={{ padding: 8 }}><input name="fullPageWidth" type="number" min={200} max={1920} value={form.fullPageWidth} onChange={handleChange} style={{ width: 80 }} /> px</td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Full Page Height:</label></td>
                              <td style={{ padding: 8 }}><input name="fullPageHeight" value={form.fullPageHeight} onChange={handleChange} style={{ width: 80 }} /> (px or 'auto')</td>
                            </tr>
                            {(!isClientMode || !form.hideSensitiveFields) && (
                              <tr>
                                <td style={{ padding: 8, textAlign: 'right' }}><label>Full Page Footer:</label></td>
                                <td style={{ padding: 8 }}><input name="fullPageFooter" value={form.fullPageFooter} onChange={handleChange} style={{ width: 300 }} /></td>
                              </tr>
                            )}
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Footer Color:</label></td>
                              <td style={{ padding: 8 }}>
                                <select name="footerColor" value={form.footerColor} onChange={handleChange} style={{ width: 100, marginRight: 8 }}>
                                  <option value="primary">Primary</option>
                                  <option value="secondary">Secondary</option>
                                  <option value="custom">Custom</option>
                                </select>
                                {form.footerColor === 'custom' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input 
                                      type="color" 
                                      name="footerCustomColor" 
                                      value={expandHex(form.footerCustomColor)} 
                                      onChange={handleChange} 
                                      style={{ width: 40, height: 30 }}
                                    />
                                    <input 
                                      type="text" 
                                      name="footerCustomColor" 
                                      value={form.footerCustomColor} 
                                      onChange={handleChange}
                                      placeholder="#ffffff"
                                      style={{ width: 80, height: 30, padding: '4px 8px', fontSize: '12px' }}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Background Color:</label></td>
                              <td style={{ padding: 8 }}>
                                <select name="backgroundFullType" value={form.backgroundFullType} onChange={handleChange} style={{ width: 100, marginRight: 8 }}>
                                  <option value="primary">Primary</option>
                                  <option value="secondary">Secondary</option>
                                  <option value="custom">Custom</option>
                                </select>
                                {form.backgroundFullType === 'custom' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input 
                                      type="color" 
                                      name="backgroundFullCustom" 
                                      value={expandHex(form.backgroundFullCustom)} 
                                      onChange={handleChange} 
                                      style={{ width: 40, height: 30 }}
                                    />
                                    <input 
                                      type="text" 
                                      name="backgroundFullCustom" 
                                      value={form.backgroundFullCustom} 
                                      onChange={handleChange}
                                      placeholder="#ffffff"
                                      style={{ width: 80, height: 30, padding: '4px 8px', fontSize: '12px' }}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>

                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Button Background:</label></td>
                              <td style={{ padding: 8 }}>
                                <select name="buttonBgFullType" value={form.buttonBgFullType} onChange={handleChange} style={{ width: 100, marginRight: 8 }}>
                                  <option value="primary">Primary</option>
                                  <option value="secondary">Secondary</option>
                                  <option value="custom">Custom</option>
                                </select>
                                {form.buttonBgFullType === 'custom' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input 
                                      type="color" 
                                      name="buttonBgFullCustom" 
                                      value={expandHex(form.buttonBgFullCustom)} 
                                      onChange={handleChange} 
                                      style={{ width: 40, height: 30 }}
                                    />
                                    <input 
                                      type="text" 
                                      name="buttonBgFullCustom" 
                                      value={form.buttonBgFullCustom} 
                                      onChange={handleChange}
                                      placeholder="#ffffff"
                                      style={{ width: 80, height: 30, padding: '4px 8px', fontSize: '12px' }}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Button Text Color:</label></td>
                              <td style={{ padding: 8 }}>
                                <select name="buttonTextFullType" value={form.buttonTextFullType} onChange={handleChange} style={{ width: 100, marginRight: 8 }}>
                                  <option value="primary">Primary</option>
                                  <option value="secondary">Secondary</option>
                                  <option value="custom">Custom</option>
                                </select>
                                {form.buttonTextFullType === 'custom' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input 
                                      type="color" 
                                      name="buttonTextFullCustom" 
                                      value={expandHex(form.buttonTextFullCustom)} 
                                      onChange={handleChange} 
                                      style={{ width: 40, height: 30 }}
                                    />
                                    <input 
                                      type="text" 
                                      name="buttonTextFullCustom" 
                                      value={form.buttonTextFullCustom} 
                                      onChange={handleChange}
                                      placeholder="#ffffff"
                                      style={{ width: 80, height: 30, padding: '4px 8px', fontSize: '12px' }}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>

                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Transcript Background:</label></td>
                              <td style={{ padding: 8 }}>
                                <select name="cardBgFullType" value={form.cardBgFullType} onChange={handleChange} style={{ width: 100, marginRight: 8 }}>
                                  <option value="primary">Primary</option>
                                  <option value="secondary">Secondary</option>
                                  <option value="custom">Custom</option>
                                </select>
                                {form.cardBgFullType === 'custom' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input 
                                      type="color" 
                                      name="cardBgFullCustom" 
                                      value={expandHex(form.cardBgFullCustom)} 
                                      onChange={handleChange} 
                                      style={{ width: 40, height: 30 }}
                                    />
                                    <input 
                                      type="text" 
                                      name="cardBgFullCustom" 
                                      value={form.cardBgFullCustom} 
                                      onChange={handleChange}
                                      placeholder="#ffffff"
                                      style={{ width: 80, height: 30, padding: '4px 8px', fontSize: '12px' }}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Transcript Text Color:</label></td>
                              <td style={{ padding: 8 }}>
                                <select name="cardTextFullType" value={form.cardTextFullType} onChange={handleChange} style={{ width: 100, marginRight: 8 }}>
                                  <option value="primary">Primary</option>
                                  <option value="secondary">Secondary</option>
                                  <option value="custom">Custom</option>
                                </select>
                                {form.cardTextFullType === 'custom' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input 
                                      type="color" 
                                      name="cardTextFullCustom" 
                                      value={expandHex(form.cardTextFullCustom)} 
                                      onChange={handleChange} 
                                      style={{ width: 40, height: 30 }}
                                    />
                                    <input 
                                      type="text" 
                                      name="cardTextFullCustom" 
                                      value={form.cardTextFullCustom} 
                                      onChange={handleChange}
                                      placeholder="#ffffff"
                                      style={{ width: 80, height: 30, padding: '4px 8px', fontSize: '12px' }}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Transcript Font Size:</label></td>
                              <td style={{ padding: 8 }}><input name="transcriptFontSize" type="number" min={10} max={24} value={form.transcriptFontSize || 14} onChange={handleChange} style={{ width: 60 }} /> px</td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Use gradient for background:</label></td>
                              <td style={{ padding: 8 }}><input type="checkbox" name="bgGradient" checked={form.bgGradient} onChange={handleChange} /> (Full Page)</td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Use gradient for mic button:</label></td>
                              <td style={{ padding: 8 }}><input type="checkbox" name="buttonGradient" checked={form.buttonGradient} onChange={handleChange} /> (Full Page)</td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Transcript Default Open:</label></td>
                              <td style={{ padding: 8 }}><input type="checkbox" name="transcriptDefaultOpenFull" checked={form.transcriptDefaultOpenFull} onChange={handleChange} /> (Full Page)</td>
                            </tr>
                            <tr>
                              <td style={{ padding: 8, textAlign: 'right' }}><label>Mic Button Sizing:</label></td>
                              <td style={{ padding: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <label style={{ fontSize: '0.9em' }}>
                                    <input 
                                      type="radio" 
                                      name="layout.full.mode" 
                                      value="fixed" 
                                      checked={form.layout?.full?.mode === 'fixed'} 
                                      onChange={handleChange}
                                      style={{ marginRight: '4px' }}
                                    />
                                    Fixed
                                  </label>
                                  <label style={{ fontSize: '0.9em' }}>
                                    <input 
                                      type="radio" 
                                      name="layout.full.mode" 
                                      value="responsive" 
                                      checked={form.layout?.full?.mode === 'responsive'} 
                                      onChange={handleChange}
                                      style={{ marginRight: '4px' }}
                                    />
                                    Responsive
                                  </label>
                                </div>
                              </td>
                            </tr>
                            {form.layout?.full?.mode === 'fixed' && (
                              <tr>
                                <td style={{ padding: 8, textAlign: 'right' }}><label>Mic Button Size:</label></td>
                                <td style={{ padding: 8 }}><input name="layout.full.size" type="number" min={40} max={190} value={form.layout?.full?.size || 160} onChange={handleChange} style={{ width: 60 }} /> px</td>
                              </tr>
                            )}
                            {form.layout?.full?.mode === 'responsive' && (
                              <>
                                <tr>
                                  <td style={{ padding: 8, textAlign: 'right' }}><label>Min Size:</label></td>
                                  <td style={{ padding: 8 }}><input name="layout.full.min" type="number" min={40} max={190} value={form.layout?.full?.min || 120} onChange={handleChange} style={{ width: 60 }} /> px</td>
                                </tr>
                                <tr>
                                  <td style={{ padding: 8, textAlign: 'right' }}><label>Max Size:</label></td>
                                  <td style={{ padding: 8 }}><input name="layout.full.max" type="number" min={40} max={190} value={form.layout?.full?.max || 190} onChange={handleChange} style={{ width: 60 }} /> px</td>
                                </tr>
                              </>
                            )}
                          </tbody>
                        </table>
                        
                        {/* Save, Done, and Preview Buttons */}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: '16px' }}>
                          <button
                            type="button"
                            onClick={handleSubmit}
                            style={{
                              padding: "10px 20px",
                              fontSize: "0.95em",
                              background: "#3a4f90",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                            onMouseOver={e => e.target.style.background = "#2c3e50"}
                            onMouseOut={e => e.target.style.background = "#3a4f90"}
                            onMouseDown={e => e.target.style.transform = "scale(0.98)"}
                            onMouseUp={e => e.target.style.transform = "scale(1)"}
                          >
                            Save Changes
                          </button>
                          
                          <button
                            type="button"
                            onClick={() => {
                              setEditId(null);
                              setError("");
                              setSuccess("");
                              setClientSubTab('general');
                            }}
                            style={{
                              padding: "10px 20px",
                              fontSize: "0.95em",
                              background: "#6c757d",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                            onMouseOver={e => e.target.style.background = "#5a6268"}
                            onMouseOut={e => e.target.style.background = "#6c757d"}
                            onMouseDown={e => e.target.style.transform = "scale(0.98)"}
                            onMouseUp={e => e.target.style.transform = "scale(1)"}
                          >
                            Done
                          </button>
                          
                          {editId && (
                            <button
                              type="button"
                              onClick={() => window.open(`/?client=${editId}&mode=full`, '_blank')}
                              style={{ 
                                padding: "10px 20px", 
                                fontSize: "0.95em", 
                                background: "linear-gradient(135deg, #3a4f90, #19647e)", 
                                color: "white", 
                                border: "1px solid #2c3e50", 
                                borderRadius: "4px", 
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "6px",
                                textDecoration: "none",
                                fontWeight: "600",
                                textShadow: "0 1px 2px rgba(0,0,0,0.3)"
                              }}
                              onMouseOver={e => e.target.style.background = "linear-gradient(135deg, #2c3e50, #19647e)"}
                              onMouseOut={e => e.target.style.background = "linear-gradient(135deg, #3a4f90, #19647e)"}
                              onMouseDown={e => e.target.style.transform = "scale(0.98)"}
                              onMouseUp={e => e.target.style.transform = "scale(1)"}
                            >
                              Full Page Preview
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Preview Panel */}
                      <div style={{
                        flex: '0 1 50%',
                        minWidth: 350,
                        display: 'flex',
                        justifyContent: 'center',
                        position: stickyFullPagePreview ? 'sticky' : 'relative',
                        top: stickyFullPagePreview ? '20px' : 'auto',
                        zIndex: stickyFullPagePreview ? 1000 : 'auto',
                        transition: 'all 0.3s ease',
                        maxHeight: stickyFullPagePreview ? 'calc(100vh - 40px)' : 'auto',
                        overflowY: stickyFullPagePreview ? 'auto' : 'visible'
                      }}>
                        <div style={{ 
                          width: '100%', 
                          minHeight: 200, 
                          background: previewBg, 
                          borderRadius: 8, 
                          marginBottom: 16,
                          position: 'relative',
                          overflow: 'hidden',
                          textAlign: 'center',
                          padding: '20px'
                        }}>
                          <h4 style={{ marginBottom: '10px', color: '#ffffff', textAlign: 'center', textShadow: '0 1px 3px rgba(0,0,0,0.8)', fontSize: '1.1em' }}>Full Page Preview</h4>
                          
                          <div style={{ textAlign: 'center', marginBottom: 15 }}>
                            <button onClick={(e) => { e.preventDefault(); setShowAvatarPreview(v => !v); }} style={{ background: '#e0e0e0', color: '#3a4f90', border: '1px solid #3a4f90', borderRadius: 6, padding: '4px 14px', fontWeight: 500, cursor: 'pointer', fontSize: '0.98em' }}>
                              {showAvatarPreview ? 'Show Mic Icon' : 'Show Avatar'}
                            </button>
                          </div>
                          
                          {/* Full Page Layout Preview */}
                          <div style={{ 
                            width: '100%', 
                            minHeight: 300, 
                            background: previewBg, 
                            borderRadius: 8, 
                            padding: '20px', 
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            textAlign: 'center'
                          }}>
                            {/* Header Container */}
                            <div style={{ 
                              width: '100%',
                              background: form.headlineBgType === 'primary' ? expandHex(form.primary) :
                                         form.headlineBgType === 'secondary' ? expandHex(form.secondary) :
                                         expandHex(form.headlineBgCustom || 'transparent'),
                              padding: '20px',
                              marginBottom: '20px',
                              borderRadius: '8px'
                            }}>
                                                              {/* Headline */}
                                <div style={{ 
                                  fontSize: `${form.headlineFontSize || 28}px`, 
                                  fontWeight: '600', 
                                  color: form.headlineColor === 'primary' ? expandHex(form.primary) : 
                                         form.headlineColor === 'secondary' ? expandHex(form.secondary) : 
                                         expandHex(form.headlineCustomColor),
                                  fontFamily: form.fontFamily,
                                  lineHeight: '1.3',
                                  marginBottom: '12px',
                                  textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                                }}>
                                  {form.fullPageHeadline || "Get your (Offer Here)!"}
                                </div>
                                
                                {/* Subheadline */}
                                <div style={{ 
                                  fontSize: `${form.subheadlineFontSize || 18}px`, 
                                  fontWeight: '400', 
                                  color: form.subheadlineColor === 'primary' ? expandHex(form.primary) : 
                                         form.subheadlineColor === 'secondary' ? expandHex(form.secondary) : 
                                         expandHex(form.subheadlineCustomColor),
                                  fontFamily: form.fontFamily,
                                  lineHeight: '1.4',
                                  textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                                }}>
                                  {form.fullPageSubheadline || "Click to talk to our AI Assistant"}
                                </div>
                                                              </div>
                            
                            {/* Mic Button Preview */}
                            <div style={{ 
                              position: 'relative', 
                              width: glowSize, 
                              height: glowSize, 
                              marginBottom: 18, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center' 
                            }}>
                              {form.showGlow && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    width: micSize * (form.glowSize || 1.2),
                                    height: micSize * (form.glowSize || 1.2),
                                    transform: 'translate(-50%, -50%)',
                                    borderRadius: '50%',
                                    background: `conic-gradient(from 0deg, ${expandHex(form.primary)}, ${expandHex(form.secondary)}, ${expandHex(form.primary)})`,
                                    zIndex: 0,
                                    filter: `blur(${form.glowBlur || 22}px) brightness(${form.glowBrightness || 1.2})`,
                                    opacity: form.glowOpacity || 0.8,
                                    animation: `adminRotateGlow ${form.glowSpeed || 7.0}s linear infinite`,
                                    pointerEvents: 'none'
                                  }}
                                />
                              )}
                              <div
                                style={{
                                  width: micSize,
                                  height: micSize,
                                  borderRadius: '50%',
                                  background: form.buttonGradient ? 
                                    `linear-gradient(315deg, ${expandHex(form.primary)} 0%, ${expandHex(form.secondary)} 100%)` : 
                                    expandHex(form.primary),
                                  border: '1px solid #fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                  zIndex: 1,
                                  position: 'relative',
                                  overflow: 'hidden'
                                }}
                              >
                                {showAvatarPreview ? (
                                  <img 
                                    src={`/avatars/${form.avatarType}`} 
                                    alt="Avatar" 
                                    style={{ 
                                      width: micSize * 0.875, 
                                      height: micSize * 0.875, 
                                      borderRadius: '50%',
                                      objectFit: 'cover'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : (
                                  <>
                                    {form.iconType === 'mic' && <MicIcon color={expandHex(form.iconColor)} size={micSize * 0.5} />}
                                    {form.iconType === 'classic-mic' && <ClassicMicIcon color={expandHex(form.iconColor)} size={micSize * 0.5} />}
                                    {form.iconType === 'headset' && <HeadsetIcon color={expandHex(form.iconColor)} size={micSize * 0.5} />}
                                    {form.iconType === 'user' && <UserIcon color={expandHex(form.iconColor)} size={micSize * 0.5} />}
                                  </>
                                )}
                              </div>
                              {form.showWaves && showAvatarPreview && [1, 2, 3].map(i => (
                                <div key={i} style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  width: micSize + i * 18,
                                  height: micSize + i * 18,
                                  borderRadius: '50%',
                                  border: `2px solid ${expandHex(form.secondary)}AA`,
                                  transform: 'translate(-50%, -50%)',
                                  zIndex: 1,
                                  pointerEvents: 'none',
                                }} />
                              ))}
                            </div>
                            
                            {/* Sample Button */}
                            <div style={{ marginBottom: 15 }}>
                              <button style={{
                                background: expandHex(getSmartButtonBgColor(
                                  form.buttonBgFullType === 'primary' ? form.primary :
                                  form.buttonBgFullType === 'secondary' ? form.secondary :
                                  form.buttonBgFullCustom || form.buttonBg || form.primary,
                                  form.backgroundFullType === 'primary' ? form.primary :
                                  form.backgroundFullType === 'secondary' ? form.secondary :
                                  form.backgroundFullCustom || form.backgroundFull || form.background,
                                  form.primary,
                                  form.secondary
                                )),
                                color: expandHex(
                                  form.buttonTextFullType === 'primary' ? form.primary :
                                  form.buttonTextFullType === 'secondary' ? form.secondary :
                                  form.buttonTextFullCustom || form.buttonText
                                ),
                                border: 'none',
                                borderRadius: 8,
                                padding: '10px 20px',
                                fontSize: '0.9em',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                Sample Button
                              </button>
                            </div>
                            
                            {/* Transcript Sample */}
                            <div style={{
                              width: '100%',
                              background: form.cardBgFullType === 'primary' ? expandHex(form.primary) :
                                         form.cardBgFullType === 'secondary' ? expandHex(form.secondary) :
                                         expandHex(form.cardBgFullCustom || form.cardBg || '#e0e0e0'),
                              color: form.cardTextFullType === 'primary' ? expandHex(form.primary) :
                                     form.cardTextFullType === 'secondary' ? expandHex(form.secondary) :
                                     expandHex(form.cardTextFullCustom || form.cardText || '#454545'),
                              padding: '12px',
                              borderRadius: 6,
                              marginBottom: '15px',
                              fontFamily: form.fontFamily,
                              fontSize: Number(form.transcriptFontSize || 14) || 14,
                              textAlign: 'left'
                            }}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>Transcript</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <div style={{ borderLeft: `4px solid ${expandHex(form.secondary)}`, paddingLeft: 10, background: 'rgba(255,255,255,0.7)', borderRadius: 6, wordWrap: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                                  {form.showRoleIcons && <span title="User" aria-label="User" style={{ marginRight: 4 }}>[User]</span>}User: Hello, this is a sample transcript message.
                                </div>
                                <div style={{ borderLeft: `4px solid ${expandHex(form.secondary)}`, paddingLeft: 10, background: 'rgba(255,255,255,0.7)', borderRadius: 6, wordWrap: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                                  {form.showRoleIcons && <span title="Agent" aria-label="Agent" style={{ marginRight: 4 }}>[Agent]</span>}Agent: Hi! How can I help you today?
                                </div>
                              </div>
                            </div>
                            
                            {/* Footer */}
                            <div style={{ 
                              fontSize: '0.8em', 
                              color: form.footerColor === 'primary' ? expandHex(form.primary) : 
                                     form.footerColor === 'secondary' ? expandHex(form.secondary) : 
                                     expandHex(form.footerCustomColor),
                              fontFamily: form.fontFamily,
                              marginTop: 'auto',
                              textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                            }}>
                              {form.fullPageFooter || "Â© 2025 Powered by Sellflows.com -- All rights reserved."}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
        </form>
            </>
          )}
          
          {/* Settings Tab Content */}
          {!isClientView && activeTab === 'settings' && (
            <>
              <h2 style={{ fontSize: "1.1em", marginBottom: 16 }}>System Settings</h2>
              
              {/* Universal Agent Status Section */}
              <div style={{ 
                background: '#e3f2fd', 
                padding: '16px', 
                borderRadius: '8px', 
                border: '1px solid #2196f3',
                marginBottom: '20px'
              }}>
                <h3 style={{ fontSize: '1em', margin: '0 0 12px 0', color: '#1976d2' }}>Universal Agent Security Settings</h3>
                <div style={{ fontSize: '0.9em', color: '#1976d2', marginBottom: '12px' }}>
                  <div><strong>Current Status:</strong></div>
                  <div>Refresh</div>
                  <div>Using Universal Agent (Shared Keys)</div>
                  <div>Retell API Key: {envStatus.retellApiKey ? 'Configured' : 'Not Set'}</div>
                  <div>Agent ID: {envStatus.agentId ? 'Configured' : 'Not Set'}</div>
                </div>
                <div style={{ 
                  background: '#fff3cd', 
                  padding: '12px', 
                  borderRadius: '4px', 
                  border: '1px solid #ffc107',
                  fontSize: '0.85em',
                  color: '#856404'
                }}>
                  <strong>Security Note:</strong> Universal Agent API keys are stored as environment variables on the server for maximum security. 
                  These keys are not accessible through the web interface and are not transmitted over HTTP. 
                  To update these keys, modify the <code>RETELL_API_KEY</code> and <code>AGENT_ID</code> in your server's .env file and restart the backend.
                </div>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32 }}>
                <div style={{ flex: '1 1 420px', minWidth: 320 }}>
                  <div style={{ 
                    background: '#f8f9fa', 
                    padding: '20px', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0',
                    marginBottom: '20px'
                  }}>
                    <h3 style={{ fontSize: "1em", marginBottom: 12, color: '#3a4f90' }}>Agency Configuration</h3>
                    
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: '500' }}>
                        Agency Name:
                      </label>
                      <input
                        type="text"
                        value={settings.agencyName}
                        onChange={(e) => setSettings({...settings, agencyName: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.9em'
                        }}
                        placeholder="Enter your agency name"
                      />
                      <div style={{ fontSize: '0.8em', color: '#666', marginTop: 4 }}>
                        This name appears in the admin panel header and client communications
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: '500' }}>
                        Custom Domain (Agency Use):
                      </label>
                      <input
                        type="text"
                        value={settings.customDomain}
                        onChange={(e) => setSettings({...settings, customDomain: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.9em'
                        }}
                        placeholder="e.g., https://widget.agency.com or https://voice.agency.com"
                      />
                      <div style={{ fontSize: '0.8em', color: '#666', marginTop: 4 }}>
                        For agency deployments: Enter the full domain where widgets will be served
                      </div>
                      
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: '500', marginTop: 16 }}>
                        Subdomain (Optional):
                      </label>
                      <input
                        type="text"
                        value={settings.subdomain}
                        onChange={(e) => setSettings({...settings, subdomain: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.9em'
                        }}
                        placeholder="e.g., widget.yourdomain.com"
                      />
                      <div style={{ fontSize: '0.8em', color: '#666', marginTop: 4 }}>
                        If deploying to a subdomain, enter it here for reference
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: '500' }}>
                        Logo Configuration:
                      </label>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', marginBottom: 4, fontSize: '0.9em', fontWeight: '400' }}>
                          Light Logo (for login pages):
                        </label>
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setSettings({...settings, lightLogo: file});
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9em'
                          }}
                        />
                        <div style={{ fontSize: '0.8em', color: '#666', marginTop: 4 }}>
                          Upload a light/white logo for login pages and dark backgrounds
                        </div>
                      </div>
                      
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', marginBottom: 4, fontSize: '0.9em', fontWeight: '400' }}>
                          Dark Logo (for admin pages):
                        </label>
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setSettings({...settings, darkLogo: file});
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9em'
                          }}
                        />
                        <div style={{ fontSize: '0.8em', color: '#666', marginTop: 4 }}>
                          Upload a dark logo for admin pages and light backgrounds
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: '500' }}>
                        Favicon Upload:
                      </label>
                      <input
                        type="file"
                        accept=".ico,.png,.jpg,.jpeg"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setSettings({...settings, favicon: file});
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.9em'
                        }}
                      />
                      <div style={{ fontSize: '0.8em', color: '#666', marginTop: 4 }}>
                        Upload a favicon (16x16, 32x32, or 64x64 recommended). Supports ICO, PNG, JPG formats.
                      </div>
                    </div>
                    
                    <button
                      onClick={async () => {
                        try {
                          // Here you would save the settings to backend
                          // For now, just update the local state
                          setAgencyName(settings.agencyName);
                          setSuccess('Settings saved successfully!');
                          setTimeout(() => setSuccess(''), 3000);
                        } catch (error) {
                          setError('Failed to save settings');
                          setTimeout(() => setError(''), 3000);
                        }
                      }}
                      style={{
                        padding: '10px 20px',
                        background: '#3a4f90',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9em',
                        fontWeight: '500'
                      }}
                    >
                      Save Settings
                    </button>
                  </div>
                  
                  <div style={{ 
                    background: '#e8f4fd', 
                    padding: '20px', 
                    borderRadius: '8px', 
                    border: '1px solid #bee5eb',
                    marginBottom: '20px'
                  }}>
                    <h3 style={{ fontSize: "1em", marginBottom: 12, color: '#19647e' }}>File Management</h3>
                    
                    <div style={{ marginBottom: 20 }}>
                      <h4 style={{ fontSize: "0.9em", marginBottom: 8, color: '#495057' }}>Avatar Management</h4>
                                              <div style={{ fontSize: '0.8em', color: '#666', marginBottom: 12 }}>
                          <strong>Specifications:</strong> PNG, JPG, JPEG | Recommended: 300x300px | Max: 2MB
                        </div>
                      <input type="file" accept=".png,.jpg,.jpeg" onChange={handleSettingsAvatarUpload} disabled={uploadingAvatar} style={{ marginBottom: 8 }} />
                      {uploadingAvatar && <div style={{ color: '#666', fontSize: '0.8em' }}>Uploading...</div>}
                      
                      <div style={{ marginTop: 12 }}>
                        <strong>Available Avatars:</strong>
                        {avatarFiles.length > 0 ? (
                          avatarFiles.map(file => (
                            <div key={file} style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              background: '#fff', 
                              margin: '4px 0', 
                              borderRadius: '4px',
                              fontSize: '0.8em',
                              fontFamily: 'monospace'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <img 
                                  src={`/${file}`} 
                                  alt={file} 
                                  style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }}
                                />
                                <span>{file}</span>
                              </div>
                              <button
                                onClick={() => handleDeleteFile(file, 'avatar')}
                                disabled={deletingFile}
                                style={{
                                  padding: '2px 6px',
                                  background: '#8b0000',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: '0.7em'
                                }}
                                title="Delete Avatar"
                              >
                                Delete
                              </button>
                            </div>
                          ))
                        ) : (
                          <div style={{ color: '#666', fontSize: '0.8em', textAlign: 'center', padding: '20px' }}>
                            No avatar files uploaded yet
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: 20 }}>
                      <h4 style={{ fontSize: "0.9em", marginBottom: 8, color: '#495057' }}>SVG Icon Management</h4>
                      <div style={{ fontSize: '0.8em', color: '#666', marginBottom: 12 }}>
                        <strong>Specifications:</strong> SVG format only | Recommended: 24x24px viewBox | Max: 100KB
                      </div>
                      <input type="file" accept=".svg" onChange={handleSettingsSvgUpload} disabled={uploadingSVG} style={{ marginBottom: 8 }} />
                      {uploadingSVG && <div style={{ color: '#666', fontSize: '0.8em' }}>Uploading...</div>}
                      
                      <div style={{ marginTop: 12 }}>
                        <strong>Available SVG Icons:</strong>
                        {svgFiles.length > 0 ? (
                          svgFiles.map(file => (
                            <div key={file} style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              background: '#fff', 
                              margin: '4px 0', 
                              borderRadius: '4px',
                              fontSize: '0.8em',
                              fontFamily: 'monospace'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                                  {file === 'classic-mic.svg' && <ClassicMicIcon color="#3a4f90" size={18} />}
                                  {file === 'modern-mic.svg' && <MicIcon color="#3a4f90" size={18} />}
                                  {file === 'headset.svg' && <HeadsetIcon color="#3a4f90" size={18} />}
                                  {file === 'user.svg' && <UserIcon color="#3a4f90" size={18} />}
                                  {!['classic-mic.svg', 'modern-mic.svg', 'headset.svg', 'user.svg'].includes(file) && (
                                    <div style={{
                                      width: 18,
                                      height: 18,
                                      background: '#f0f0f0',
                                      borderRadius: '2px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '8px',
                                      color: '#666',
                                      fontWeight: 'bold'
                                    }}>
                                      SVG
                                    </div>
                                  )}
                                </div>
                                <span>{file}</span>
                              </div>
                              <button
                                onClick={() => handleDeleteFile(file, 'svg')}
                                disabled={deletingFile}
                                style={{
                                  padding: '2px 6px',
                                  background: '#8b0000',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: '0.7em'
                                }}
                                title="Delete SVG"
                              >
                                Delete
                              </button>
                            </div>
                          ))
                        ) : (
                          <div style={{ color: '#666', fontSize: '0.8em', textAlign: 'center', padding: '20px' }}>
                            No SVG files uploaded yet
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ 
                    background: '#e8f4fd', 
                    padding: '20px', 
                    borderRadius: '8px', 
                    border: '1px solid #bee5eb'
                  }}>
                    <h3 style={{ fontSize: "1em", marginBottom: 12, color: '#19647e' }}>Deployment Information</h3>
                    
                    <div style={{ marginBottom: 12 }}>
                      <strong>Current Domain:</strong>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.85em', color: '#666', marginTop: 4 }}>
                        {window.location.origin}
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: 12 }}>
                      <strong>Admin Panel URL:</strong>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.85em', color: '#666', marginTop: 4 }}>
                        {window.location.origin}/admin
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: 12 }}>
                      <strong>Widget Embed Base URL:</strong>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.85em', color: '#666', marginTop: 4 }}>
                        {window.location.origin}/?client=CLIENT_ID&mode=widget
                      </div>
                    </div>
                    
                    <div style={{ fontSize: '0.8em', color: '#666', marginTop: 12 }}>
                      <strong>Note:</strong> When deploying to production, update these URLs in your client configurations.
                    </div>
                  </div>
                </div>
                
                <div style={{ flex: '1 1 300px', minWidth: 280 }}>
                  <div style={{ 
                    background: '#fff3cd', 
                    padding: '20px', 
                    borderRadius: '8px', 
                    border: '1px solid #ffeaa7'
                  }}>
                    <h3 style={{ fontSize: "1em", marginBottom: 12, color: '#856404' }}>System Status</h3>
                    
                    <div style={{ marginBottom: 12 }}>
                      <strong>Backend Status:</strong>
                      <span style={{ color: '#28a745', marginLeft: 8 }}>Connected</span>
                    </div>
                    
                    <div style={{ marginBottom: 12 }}>
                      <strong>Database:</strong>
                      <span style={{ color: '#28a745', marginLeft: 8 }}>Active</span>
                    </div>
                    
                    <div style={{ marginBottom: 12 }}>
                      <strong>File Uploads:</strong>
                      <span style={{ color: '#28a745', marginLeft: 8 }}>Available</span>
                    </div>
                    
                    <div style={{ marginBottom: 12 }}>
                      <strong>Email Service:</strong>
                      <span style={{ color: '#28a745', marginLeft: 8 }}>Configured</span>
                    </div>
                    
                    <div style={{ fontSize: '0.8em', color: '#666', marginTop: 16 }}>
                      <strong>Last Updated:</strong> {new Date().toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Clients Tab Content */}
          {!isClientView && activeTab === 'clients' && (
            <>
        {/* Search and sort controls above the table */}
        <div style={{ margin: "10px 0", display: "flex", gap: "10px", alignItems: "center" }}>
          <input
            type="text"
            placeholder="Search by client ID or company name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 260, padding: 6, borderRadius: 6, border: "1px solid #ccc", fontSize: "0.95em" }}
          />
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            style={{ padding: 6, borderRadius: 6, border: "1px solid #ccc", fontSize: "0.95em" }}
          >
            <option value="recent">Most Recent First</option>
            <option value="company">Company Name A-Z</option>
          </select>
        </div>
        <h2 style={{ fontSize: "1em", marginBottom: 6 }}>All Clients</h2>
        {loading ? <div>Loading...</div> : (
          <div style={{ overflowX: "auto", maxWidth: "100%" }}>
                  <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9em', color: '#666' }}>
                      {Object.keys(clients).length} client{Object.keys(clients).length !== 1 ? 's' : ''} configured
                    </span>
                    <button
                      onClick={() => {
                        handleTabChange('edit');
                        setForm({
                          clientId: "",
                          companyName: "",
                          agentId: "",
                          primary: "#bbbbbb",
                          secondary: "#000000",
                          background: "#aaaaaa",
                          transparentWidgetBg: true,
                          bgGradient: false,
                          buttonGradient: false,
                          cardBg: "#e0e0e0",
                          cardText: "#454545",
                          buttonBg: "",
                          buttonText: "#ffffff",
                          buttonHover: "",
                          transcriptDefaultOpenFull: true,
                          transcriptDefaultOpenWidget: false,
                          transparentBackgroundFull: false,
                          transparentBackgroundWidget: true,
                          fontFamily: "Montserrat, Arial, sans-serif",
                          fontSize: "14",
                          iconType: "classic-mic",
                          iconColor: "#ffffff",
                          avatarType: "avatar.png",
                          showRoleIcons: false,
                          minSize: 120,
                          defaultSize: 160,
                          maxSize: 190,
                          layout: {
                            widget: { mode: 'fixed', size: 160, min: 120, max: 190 },
                            full: { mode: 'fixed', size: 160, min: 120, max: 190 },
                          },
                          showGlow: true,
                          showWaves: true,
                          fullPageWidth: 800,
                          fullPageHeight: 'auto',
                          fullPageHeadline: "Get your (Offer Here)!",
                          fullPageSubheadline: "Click to talk to our AI Assistant",
                          fullPageFooter: "Â© 2025 Powered by Sellflows.com -- All rights reserved.",
                          headlineFontSize: 28,
                          subheadlineFontSize: 18,
                          headlineColor: "primary",
                          headlineCustomColor: "#6ec6ff",
                          subheadlineColor: "secondary",
                          subheadlineCustomColor: "#ff6ec4",
                          footerColor: "secondary",
                          footerCustomColor: "#ffffff",
                          backgroundFull: "",

                          buttonBgFull: "",
                          buttonTextFull: "#ffffff",
                          cardBgFull: "",
                          cardTextFull: "",
                          password: "",
                          hideSensitiveFields: true,
                          autoSendPasswordLink: false,
                          backgroundFullType: "primary",
                          buttonBgFullType: "primary",
                          buttonTextFullType: "primary",
                          cardBgFullType: "primary",
                          cardTextFullType: "primary",
                          backgroundFullCustom: "",
                          buttonBgFullCustom: "",
                          buttonTextFullCustom: "",
                          cardBgFullCustom: "",
                          cardTextFullCustom: "",
                          transcriptFontSize: 14,
                        });
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#3a4f90',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        fontWeight: '500'
                      }}
                    >
                      Add Client
                    </button>
                  </div>
            <table border="1" cellPadding="6" style={{ width: "100%", minWidth: 500, borderCollapse: "collapse", fontSize: "0.82em" }}>
              <thead>
                <tr>
                  <th style={{ fontSize: "0.95em", padding: 6, maxWidth: 90, whiteSpace: "normal", wordBreak: "break-word" }}>Company Name</th>
                  <th style={{ fontSize: "0.95em", padding: 6, maxWidth: 80, whiteSpace: "normal", wordBreak: "break-word" }}>Client ID</th>
                  <th style={{ fontSize: "0.95em", padding: 6, maxWidth: 40, whiteSpace: "normal", wordBreak: "break-word" }}>Primary</th>
                  <th style={{ fontSize: "0.95em", padding: 6, maxWidth: 40, whiteSpace: "normal", wordBreak: "break-word" }}>Secondary</th>
                  <th style={{ fontSize: "0.95em", padding: 6, maxWidth: 60, whiteSpace: "normal", wordBreak: "break-word" }}>Mic/Avatar</th>
                        <th style={{ fontSize: "0.95em", padding: 6, maxWidth: 120, whiteSpace: "normal", wordBreak: "break-word" }}>Links</th>
                        <th style={{ fontSize: "0.95em", padding: 6, maxWidth: 120, whiteSpace: "normal", wordBreak: "break-word" }}>Client Access</th>
                  <th style={{ fontSize: "0.95em", padding: 6, maxWidth: 70, whiteSpace: "normal", wordBreak: "break-word" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(sortBy === 'recent' 
                  ? Object.entries(clients)
                      .filter(([id, c]) =>
                        id.toLowerCase().includes(search.toLowerCase()) ||
                        (c.companyName || "").toLowerCase().includes(search.toLowerCase())
                      )
                      .reverse() // Show most recent first
                  : Object.entries(clients)
                      .filter(([id, c]) =>
                        id.toLowerCase().includes(search.toLowerCase()) ||
                        (c.companyName || "").toLowerCase().includes(search.toLowerCase())
                      )
                      .sort(([idA, cA], [idB, cB]) => {
                        const nameA = (cA.companyName || "").toLowerCase();
                        const nameB = (cB.companyName || "").toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                ).map(([id, c]) => (
                    <tr key={id}>
                      <td style={{ maxWidth: 90, whiteSpace: "normal", wordBreak: "break-word" }}>{c.companyName}</td>
                      <td style={{ maxWidth: 80, whiteSpace: "normal", wordBreak: "break-word" }}>{id}</td>
                      <td style={{ maxWidth: 40, textAlign: "center" }}>
                        <span style={{ display: "inline-block", width: 18, height: 18, borderRadius: "50%", background: c.ui?.colors?.primary || "#ccc", border: "1px solid #bbb" }} title={c.ui?.colors?.primary}></span>
                      </td>
                      <td style={{ maxWidth: 40, textAlign: "center" }}>
                        <span style={{ display: "inline-block", width: 18, height: 18, borderRadius: "50%", background: c.ui?.colors?.secondary || "#ccc", border: "1px solid #bbb" }} title={c.ui?.colors?.secondary}></span>
                      </td>
                      <td style={{ maxWidth: 60, textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                          {/* Mic icon first */}
                          <div style={{ display: "inline-block" }}>
                            {c.ui?.icon?.type === 'mic' && <MicIcon color={c.ui?.icon?.color || c.ui?.colors?.primary || "#3a4f90"} size={16} />}
                            {c.ui?.icon?.type === 'classic-mic' && <span style={{ display: 'inline-block', background: c.ui?.colors?.primary || '#3a4f90', borderRadius: '50%', width: 20, height: 20, verticalAlign: 'middle', marginRight: 2 }}><MicIcon color="#fff" size={14} style={{ display: 'block', margin: '3px auto' }} /></span>}
                            {c.ui?.icon?.type === 'headset' && <HeadsetIcon color={c.ui?.icon?.color || "#3a4f90"} size={16} />}
                            {c.ui?.icon?.type === 'user' && <UserIcon color={c.ui?.icon?.color || "#3a4f90"} size={16} />}
                            {!c.ui?.icon?.type && <MicIcon color="#3a4f90" size={16} />}
                          </div>
                          {/* Avatar second */}
                          <img 
                            src={`/${c.ui?.avatar || "avatar.png"}`} 
                            alt="Avatar" 
                            style={{ width: 16, height: 16, borderRadius: "50%", objectFit: "cover" }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      </td>
                            <td style={{ maxWidth: 120, whiteSpace: "normal", wordBreak: "break-word" }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <a
                          href={`/?client=${id}&mode=widget`}
                          target="_blank"
                          rel="noopener noreferrer"
                                  style={{ color: '#0066cc', textDecoration: 'underline', fontWeight: 500, fontSize: '0.8em' }}
                                >
                                  Widget
                                </a>
                                <a
                                  href={`/?client=${id}&mode=full`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#0066cc', textDecoration: 'underline', fontWeight: 500, fontSize: '0.8em' }}
                                >
                                  Full Page
                                </a>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    // Go directly to the comprehensive iframe demo page
                                    const baseUrl = settings.customDomain || window.location.origin;
                                    window.open(window.location.origin + '/iframe-demo?client=' + id + '&returnTo=admin', '_blank');
                                  }}
                                  style={{ color: '#0066cc', textDecoration: 'underline', fontWeight: 500, fontSize: '0.8em' }}
                                  title="Preview embed code"
                                >
                                  Widget Iframe
                                </button>
                              </div>
                            </td>
                            <td style={{ maxWidth: 120, whiteSpace: "normal", wordBreak: "break-word" }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <a
                                  href={`/client/${id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ 
                                    color: '#0066cc', 
                                    textDecoration: 'underline', 
                                    fontWeight: 500, 
                                    fontSize: '0.8em' 
                                  }}
                                  title="Copy this link to send to client"
                                >
                                  Client Login
                                </a>
                                <button
                                  onClick={() => {
                                    const baseUrl = settings.customDomain || window.location.origin;
                                    navigator.clipboard.writeText(`${baseUrl}/client/${id}`);
                                    setSuccess('Client access link copied!');
                                    setTimeout(() => setSuccess(''), 3000);
                                  }}
                                  style={{
                                    padding: '2px 6px',
                                    background: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    fontSize: '0.7em'
                                  }}
                                  title="Copy link to clipboard"
                                >
                                  Copy Link
                                </button>
                                <button
                                  onClick={() => handleGeneratePasswordLink(id)}
                                  style={{
                                    padding: '2px 6px',
                                    background: c.password ? '#ffc107' : '#17a2b8',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    fontSize: '0.7em'
                                  }}
                                  title={c.password ? 'Generate password reset link (requires email)' : 'Generate password setup link'}
                                >
                                  {c.password ? 'Reset PW' : 'Setup PW'}
                                </button>
                              </div>
                      </td>
                      <td style={{ maxWidth: 70, whiteSpace: "normal", wordBreak: "break-word" }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <button onClick={() => handleEdit(id)} style={{ padding: '4px 8px', background: '#007bff', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.8em', fontWeight: '500' }}>Edit</button>
                                <button onClick={() => handleDelete(id)} style={{ padding: '2px 6px', background: '#8b0000', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.7em' }}>Delete</button>
                                {c.email && (
                                  <button 
                                    onClick={async () => {
                                      try {
                                        const response = await fetch(`${API_BASE}/send-welcome-email`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ clientId: id })
                                        });
                                        const data = await response.json();
                                        if (data.success) {
                                          setSuccess('Welcome email sent successfully!');
                                          setTimeout(() => setSuccess(''), 3000);
                                        } else {
                                          setError(data.message || 'Failed to send email');
                                          setTimeout(() => setError(''), 3000);
                                        }
                                      } catch (error) {
                                        setError('Failed to send email');
                                        setTimeout(() => setError(''), 3000);
                                      }
                                    }}
                                    style={{
                                      padding: '2px 6px',
                                      background: '#28a745',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      fontSize: '0.7em'
                                    }}
                                    title="Send welcome email to client"
                                  >
                                    Send Email
                                  </button>
                                )}
                              </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
              )}
            </>
        )}
      </div>
      )}
      
      {/* Fixed position success/error messages at bottom of screen */}
      {success && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#28a745',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 1000,
          fontSize: '14px',
          fontWeight: '500',
          maxWidth: '80%',
          textAlign: 'center'
        }}>
          {success}
        </div>
      )}
      
      {error && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#dc3545',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 1000,
          fontSize: '14px',
          fontWeight: '500',
          maxWidth: '80%',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

// Wrap the AdminPanel with ErrorBoundary to catch ResizeObserver errors
const AdminPanelWithErrorBoundary = () => (
  <ErrorBoundary>
    <AdminPanel />
  </ErrorBoundary>
);

export default AdminPanelWithErrorBoundary;






